<template>
  <div class="participant-monitoring">
    <!-- 필터 섹션 -->
    <div class="filter-section mb-4">
      <!-- 필터 헤더 (항상 보임) -->
      <div class="filter-header">
        <a-row :gutter="16" align="middle" justify="space-between">
          <a-col>
            <span class="filter-title">
              <i class="fa fa-filter"></i>
              필터 옵션
              <a-badge v-if="activeFilterCount > 0" :count="activeFilterCount" :number-style="{ backgroundColor: '#52c41a', fontSize: '12px' }" style="margin-left: 8px;" />
            </span>
          </a-col>
          <a-col>
            <a-space>
              <a-tooltip v-if="activeFilterCount > 0" title="모든 필터 초기화">
                <a-button type="primary" ghost @click="resetAllFilters">
                  <template #icon>
                    <i class="fa fa-eraser"></i>
                  </template>
                  필터 초기화
                </a-button>
              </a-tooltip>
              <a-button type="primary" ghost @click="handleRefresh">
                <template #icon>
                  <i class="fa fa-refresh"></i>
                </template>
                새로고침
              </a-button>
              <a-button type="text" @click="isFilterCollapsed = !isFilterCollapsed" class="collapse-button">
                <template #icon>
                  <i :class="isFilterCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'"></i>
                </template>
                {{ isFilterCollapsed ? '펼치기' : '접기' }}
              </a-button>
            </a-space>
          </a-col>
        </a-row>

        <!-- 접힌 상태에서 적용된 필터 태그 표시 -->
        <div v-if="isFilterCollapsed && activeFilterTags.length > 0" class="filter-tags-summary">
          <template v-for="tag in activeFilterTags" :key="tag.key">
            <!-- 이벤트 태그는 ID와 함께 표시 -->
            <div v-if="tag.key === 'event'" class="event-tag-with-id">
              <a-tooltip :title="tag.fullLabel">
                <a-tag :color="tag.color" size="small" class="event-tag-label">
                  {{ tag.fullLabel || tag.label }}
                </a-tag>
              </a-tooltip>
              <a-tooltip title="이벤트 ID 복사">
                <a-button
                  type="text"
                  size="small"
                  class="copy-id-button"
                  @click="copyEventId"
                >
                  <template #icon>
                    <i class="fa fa-copy"></i>
                  </template>
                  {{ tag.eventId }}
                </a-button>
              </a-tooltip>
            </div>
            <!-- 일반 태그 -->
            <a-tooltip v-else :title="tag.fullLabel || tag.label">
              <a-tag :color="tag.color" size="small">
                {{ tag.label }}
              </a-tag>
            </a-tooltip>
          </template>
        </div>
      </div>

      <!-- 접을 수 있는 필터 콘텐츠 -->
      <div v-show="!isFilterCollapsed" class="filter-content">
        <!-- 첫 번째 줄: 이벤트 필터 -->
        <a-row :gutter="[16, 12]" align="middle" class="filter-row mt-3">
        <a-col :xs="24" :sm="24" :md="12" :lg="6" :xl="6">
          <label class="filter-label">
            이벤트
            <span v-if="selectedEvent" class="event-id-display">
              <a-typography-text
                :copyable="{ text: selectedEvent }"
                style="color: #666; font-size: 12px;"
              >
                (ID: {{ selectedEvent }})
              </a-typography-text>
            </span>
          </label>
          <a-select
            v-model:value="selectedEvent"
            placeholder="이벤트를 선택해주세요."
            style="width: 100%"
            :loading="eventsLoading"
            @change="handleEventChange"
            allow-clear
            :dropdown-match-select-width="false"
            :dropdown-style="{ minWidth: '400px', maxWidth: '600px' }"
            popup-class-name="event-select-dropdown"
          >
            <a-select-option v-for="event in eventList" :key="event._id" :value="event._id">
              {{ event.eventTitle }} ({{ getEventStatusText(event.eventStatus) }})
            </a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="24" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            주요 고객분류
            <a-tooltip title="Primary 세그먼트 (CERTIFICATE_HOLDER: 인증서 보유, NON_CERTIFICATE: 인증서 미보유)">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedPrimarySegment"
            placeholder="전체"
            style="width: 100%"
            @change="handlePrimarySegmentChange"
            allow-clear
          >
            <a-select-option value="CERTIFICATE_HOLDER">인증서 보유 (바이럴)</a-select-option>
            <a-select-option value="NON_CERTIFICATE">인증서 미보유 (잠재고객)</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="24" :sm="12" :md="6" :lg="6" :xl="6">
          <label class="filter-label">
            세부 고객분류
            <a-tooltip title="Secondary 세그먼트 (복수 선택 가능: EXISTING_CERTIFICATE, NEW_CERTIFICATE, EVENT_PARTICIPANT 등)">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedSecondarySegment"
            mode="multiple"
            placeholder="전체"
            style="width: 100%"
            @change="handleSecondarySegmentChange"
            allow-clear
            :max-tag-count="2"
          >
            <a-select-option value="EXISTING_CERTIFICATE">기존 인증서</a-select-option>
            <a-select-option value="NEW_CERTIFICATE">신규 인증서</a-select-option>
            <a-select-option value="EVENT_PARTICIPANT">이벤트 참여 (응모정보 제출)</a-select-option>
            <a-select-option value="THERMAGE">써마지</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="24" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            과거 이벤트 참여 이력
            <a-tooltip title="이전에 다른 이벤트에 참여한 이력 여부">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedEventMetadataFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleEventMetadataFilterChange"
            allow-clear
          >
            <a-select-option value="exists">참여 이력 있음</a-select-option>
            <a-select-option value="notExists">참여 이력 없음(신규)</a-select-option>
          </a-select>
        </a-col>
      </a-row>
      
      <!-- 두 번째 줄: 쿠폰, 리뷰 및 마케팅 필터 추가 -->
      <a-row :gutter="[16, 12]" align="middle" class="filter-row mt-3">
        <a-col :xs="12" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            쿠폰 발급 권한
            <a-tooltip title="couponInfo 객체가 있는 경우">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedCouponInfo"
            placeholder="전체"
            style="width: 100%"
            @change="handleCouponInfoChange"
            allow-clear
          >
            <a-select-option value="exists">권한 있음</a-select-option>
            <a-select-option value="notExists">권한 없음</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="3" :xl="3">
          <label class="filter-label">
            쿠폰 수령 여부
            <a-tooltip title="couponInfo.couponNumber 가 있는 경우">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedCouponNumber"
            placeholder="전체"
            style="width: 100%"
            @change="handleCouponNumberChange"
            :disabled="selectedCouponInfo !== 'exists'"
            allow-clear
          >
            <a-select-option value="exists">수령함</a-select-option>
            <a-select-option value="notExists">미수령</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="3" :xl="3">
          <label class="filter-label">
            최소 쿠폰 수
            <a-tooltip title="couponInfo 배열의 갯수">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-input-number
            v-model:value="minCouponCount"
            :min="1"
            placeholder="쿠폰 수량"
            style="width: 100%"
            @change="handleMinCouponCountChange"
          />
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            리뷰 유무
            <a-tooltip title="리뷰 배열 자체의 존재 여부를 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedReviewFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleReviewFilterChange"
            allow-clear
          >
            <a-select-option value="exists">있음</a-select-option>
            <a-select-option value="notExists">없음</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            리뷰 내용 유무
            <a-tooltip title="리뷰 내용(content)의 존재 여부를 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedReviewContentFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleReviewContentFilterChange"
            :disabled="selectedReviewFilter !== 'exists'"
            allow-clear
          >
            <a-select-option value="exists">내용 있음</a-select-option>
            <a-select-option value="notExists">내용 없음</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            마케팅 수신 동의
            <a-tooltip title="마케팅 수신 동의 여부로 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedMarketingConsent"
            placeholder="전체"
            style="width: 100%"
            @change="handleMarketingConsentChange"
            allow-clear
          >
            <a-select-option value="agreed">동의</a-select-option>
            <a-select-option value="revoked">동의안함</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            삭제 여부
            <a-tooltip title="삭제된 고객 표시 여부를 선택합니다">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedDeleteYnFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleDeleteYnFilterChange"
          >
            <a-select-option :value="null">전체</a-select-option>
            <a-select-option value="exclude">삭제 고객 제외</a-select-option>
            <a-select-option value="only">삭제된 고객만</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="4" :xl="4">
          <label class="filter-label">
            공유 여부
            <a-tooltip title="카카오톡 공유 이력이 있는지 여부로 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedShareFilter"
            placeholder="전체"
            style="width: 100%"
            @change="handleShareFilterChange"
            allow-clear
          >
            <a-select-option value="exists">공유함</a-select-option>
            <a-select-option value="notExists">미공유</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="12" :sm="12" :md="6" :lg="3" :xl="3">
          <label class="filter-label">
            공유 횟수
            <a-tooltip title="공유 횟수 조건으로 참여자를 필터링">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-input-group compact style="display: flex">
            <a-select
              v-model:value="shareCountOperator"
              style="width: 80px"
              :disabled="selectedShareFilter === 'notExists'"
              @change="handleShareCountOperatorChange"
            >
              <a-select-option value="gte">이상</a-select-option>
              <a-select-option value="lte">이하</a-select-option>
            </a-select>
            <a-input-number
              v-model:value="minShareCount"
              :min="1"
              placeholder="횟수"
              style="flex: 1"
              @change="handleMinShareCountChange"
              :disabled="selectedShareFilter === 'notExists'"
            />
          </a-input-group>
        </a-col>
      </a-row>

      <!-- 세 번째 줄: 액티비티 유형 (AARRR 그룹핑) -->
      <a-row :gutter="16" align="middle" class="filter-row mt-3" v-if="availableActivityTypes.length > 0">
        <a-col :span="24">
          <label class="filter-label">
            액티비티 유형 (AARRR 퍼널 그룹)
            <a-tooltip title="이벤트의 AARRR 메트릭 설정에 따라 그룹화된 액티비티 유형">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <div class="activity-checkbox-group">
            <a-checkbox-group
              v-model:value="selectedActivityTypes"
              @change="handleActivityTypeChange"
              style="width: 100%"
            >
            <div class="aarrr-groups-container">
              <!-- Acquisition 그룹 -->
              <div v-if="groupedActivityTypes.acquisition.length > 0" class="activity-group">
                <div class="group-header" @click="toggleGroup('acquisition')" style="cursor: pointer;">
                  <i :class="collapsedGroups.acquisition ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" class="collapse-icon"></i>
                  <i class="fa fa-users"></i>
                  <span class="group-title">Acquisition (획득)</span>
                  <a-tag color="blue" size="small">{{ groupedActivityTypes.acquisition.length }}개 ({{ groupTotalCounts.acquisition.toLocaleString() }}건)</a-tag>
                </div>
                <div class="group-content" v-show="!collapsedGroups.acquisition">
                  <div
                    v-for="activityType in groupedActivityTypes.acquisition"
                    :key="activityType.type"
                    class="activity-item"
                  >
                    <a-checkbox :value="activityType.type">
                      <span class="activity-label">{{ activityType.label }}</span>
                    </a-checkbox>
                  </div>
                </div>
              </div>

              <!-- Activation 그룹 -->
              <div v-if="groupedActivityTypes.activation.length > 0" class="activity-group">
                <div class="group-header" @click="toggleGroup('activation')" style="cursor: pointer;">
                  <i :class="collapsedGroups.activation ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" class="collapse-icon"></i>
                  <i class="fa fa-hand-pointer-o"></i>
                  <span class="group-title">Activation (활성화)</span>
                  <a-tag color="green" size="small">{{ groupedActivityTypes.activation.length }}개 ({{ groupTotalCounts.activation.toLocaleString() }}건)</a-tag>
                </div>
                <div class="group-content" v-show="!collapsedGroups.activation">
                  <div
                    v-for="activityType in groupedActivityTypes.activation"
                    :key="activityType.type"
                    class="activity-item"
                  >
                    <a-checkbox :value="activityType.type">
                      <span class="activity-label">{{ activityType.label }}</span>
                    </a-checkbox>
                  </div>
                </div>
              </div>

              <!-- Retention 그룹 -->
              <div v-if="groupedActivityTypes.retention.length > 0" class="activity-group">
                <div class="group-header" @click="toggleGroup('retention')" style="cursor: pointer;">
                  <i :class="collapsedGroups.retention ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" class="collapse-icon"></i>
                  <i class="fa fa-repeat"></i>
                  <span class="group-title">Retention (유지)</span>
                  <a-tag color="orange" size="small">{{ groupedActivityTypes.retention.length }}개 ({{ groupTotalCounts.retention.toLocaleString() }}건)</a-tag>
                </div>
                <div class="group-content" v-show="!collapsedGroups.retention">
                  <div
                    v-for="activityType in groupedActivityTypes.retention"
                    :key="activityType.type"
                    class="activity-item"
                  >
                    <a-checkbox :value="activityType.type">
                      <span class="activity-label">{{ activityType.label }}</span>
                    </a-checkbox>
                  </div>
                </div>
              </div>

              <!-- Revenue 그룹 -->
              <div v-if="groupedActivityTypes.revenue.length > 0" class="activity-group">
                <div class="group-header" @click="toggleGroup('revenue')" style="cursor: pointer;">
                  <i :class="collapsedGroups.revenue ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" class="collapse-icon"></i>
                  <i class="fa fa-dollar"></i>
                  <span class="group-title">Revenue (수익)</span>
                  <a-tag color="red" size="small">{{ groupedActivityTypes.revenue.length }}개 ({{ groupTotalCounts.revenue.toLocaleString() }}건)</a-tag>
                </div>
                <div class="group-content" v-show="!collapsedGroups.revenue">
                  <div
                    v-for="activityType in groupedActivityTypes.revenue"
                    :key="activityType.type"
                    class="activity-item"
                  >
                    <a-checkbox :value="activityType.type">
                      <span class="activity-label">{{ activityType.label }}</span>
                    </a-checkbox>
                  </div>
                </div>
              </div>

              <!-- Referral 그룹 -->
              <div v-if="groupedActivityTypes.referral.length > 0" class="activity-group">
                <div class="group-header" @click="toggleGroup('referral')" style="cursor: pointer;">
                  <i :class="collapsedGroups.referral ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" class="collapse-icon"></i>
                  <i class="fa fa-share-alt"></i>
                  <span class="group-title">Referral (추천)</span>
                  <a-tag color="purple" size="small">{{ groupedActivityTypes.referral.length }}개 ({{ groupTotalCounts.referral.toLocaleString() }}건)</a-tag>
                </div>
                <div class="group-content" v-show="!collapsedGroups.referral">
                  <div
                    v-for="activityType in groupedActivityTypes.referral"
                    :key="activityType.type"
                    class="activity-item"
                  >
                    <a-checkbox :value="activityType.type">
                      <span class="activity-label">{{ activityType.label }}</span>
                    </a-checkbox>
                  </div>
                </div>
              </div>

              <!-- 미분류 그룹 -->
              <div v-if="groupedActivityTypes.uncategorized.length > 0" class="activity-group">
                <div class="group-header" @click="toggleGroup('uncategorized')" style="cursor: pointer;">
                  <i :class="collapsedGroups.uncategorized ? 'fa fa-chevron-right' : 'fa fa-chevron-down'" class="collapse-icon"></i>
                  <i class="fa fa-question-circle"></i>
                  <span class="group-title">미분류</span>
                  <a-tag color="default" size="small">{{ groupedActivityTypes.uncategorized.length }}개 ({{ groupTotalCounts.uncategorized.toLocaleString() }}건)</a-tag>
                </div>
                <div class="group-content" v-show="!collapsedGroups.uncategorized">
                  <div
                    v-for="activityType in groupedActivityTypes.uncategorized"
                    :key="activityType.type"
                    class="activity-item"
                  >
                    <a-checkbox :value="activityType.type">
                      <span class="activity-label">{{ activityType.label }}</span>
                    </a-checkbox>
                  </div>
                </div>
              </div>
            </div>
            </a-checkbox-group>
          </div>
        </a-col>
      </a-row>
      
      <!-- 네 번째 줄: 날짜 필터 -->
      <a-row :gutter="[16, 12]" align="middle" class="filter-row mt-3">
        <a-col :xs="24" :sm="12" :md="8" :lg="5" :xl="5">
          <label class="filter-label">
            날짜 필터 유형
            <a-tooltip title="날짜 필터 기준 선택 (한 번에 하나만 적용 가능)">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-select
            v-model:value="selectedDateType"
            placeholder="이벤트 참여시간"
            style="width: 100%"
            @change="handleDateTypeChange"
            allow-clear
          >
            <a-select-option value="participatedAt">이벤트 참여시간</a-select-option>
            <a-select-option value="surgeryDate">수술일자</a-select-option>
            <a-select-option value="couponCreatedAt">쿠폰 정보 생성시간</a-select-option>
            <a-select-option value="couponIssuedAt">쿠폰 발급시간</a-select-option>
            <a-select-option value="couponExpiryDate">쿠폰 만료일</a-select-option>
          </a-select>
        </a-col>
        <a-col :xs="24" :sm="12" :md="10" :lg="7" :xl="7">
          <label class="filter-label">
            날짜 범위
            <a-tooltip title="선택한 날짜 필터 유형에 대한 기간 설정">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-range-picker
            v-model:value="selectedDateRange"
            style="width: 100%"
            :placeholder="['시작일', '종료일']"
            format="YYYY-MM-DD"
            @change="handleDateRangeChange"
            :disabled="!selectedDateType"
          />
        </a-col>
        <a-col :xs="0" :sm="0" :md="6" :lg="12" :xl="12">
          <!-- 빈 공간 - 버튼들은 5번째 행으로 이동 -->
        </a-col>
      </a-row>

      <!-- 다섯 번째 줄: 검색 필터 및 버튼들 -->
      <a-row :gutter="[16, 12]" align="middle" class="filter-row mt-3">
        <a-col :xs="24" :sm="12" :md="8" :lg="5" :xl="5">
          <label class="filter-label">
            검색
            <a-tooltip title="이름, 전화번호 검색">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-input-search
            v-model:value="searchKeyword"
            placeholder="이름, 전화번호로 검색"
            style="width: 100%"
            @search="handleSearch"
            @input="formatPhoneNumberInput"
          />
          <a-checkbox
            v-model:checked="searchAllEvents"
            style="margin-top: 8px;"
            @change="handleSearchAllEventsChange"
          >
            전체 이벤트 검색
            <a-tooltip title="체크 시 특정 이벤트가 아닌 전체 이벤트의 참여자를 검색합니다">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </a-checkbox>
        </a-col>
        <a-col :xs="24" :sm="12" :md="8" :lg="5" :xl="5">
          <label class="filter-label">
            병원 검색
            <a-tooltip title="병원 이름으로 검색 (부분 일치)">
              <i class="fa fa-question-circle help-icon"></i>
            </a-tooltip>
          </label>
          <a-input-search
            v-model:value="hospitalSearchKeyword"
            placeholder="병원 이름으로 검색"
            style="width: 100%"
            @search="handleHospitalSearch"
            allow-clear
            @change="handleHospitalSearchChange"
          />
          <!-- 높이 맞춤용 빈 공간 -->
          <div style="height: 30px;"></div>
        </a-col>
        <a-col :xs="24" :sm="24" :md="8" :lg="14" :xl="14" class="filter-actions-col">
          <a-space wrap class="filter-actions-space">
            <a-button type="text" disabled v-if="activeFilterCount > 0" class="filter-count-text">
              {{ activeFilterCount }}개 필터 적용중
            </a-button>
            <a-tooltip title="모든 필터 초기화">
              <a-badge :count="activeFilterCount" :number-style="{ backgroundColor: '#52c41a' }">
                <a-button @click="resetAllFilters">
                  <template #icon><i class="fa fa-eraser"></i></template>
                  <span class="btn-text">필터 초기화</span>
                </a-button>
              </a-badge>
            </a-tooltip>
            <a-button @click="handleRefresh">
              <template #icon><i class="fa fa-refresh"></i></template>
              <span class="btn-text">새로고침</span>
            </a-button>
          </a-space>
        </a-col>
      </a-row>
      </div> <!-- filter-content 닫기 -->
    </div>

    <!-- 이벤트 기본정보 섹션 -->
    <div v-if="currentEventData" class="event-info-section mb-4">
      <!-- 이벤트 기본정보 헤더 (항상 보임) -->
      <div class="event-info-header">
        <a-row :gutter="16" align="middle" justify="space-between">
          <a-col :flex="1">
            <div class="event-info-header-left">
              <span class="event-info-title">
                <i class="fa fa-info-circle"></i>
                이벤트 기본정보
                <a-tag :color="getEventStatusColor(currentEventData.eventStatus)" style="margin-left: 8px;">
                  {{ getEventStatusText(currentEventData.eventStatus) }}
                </a-tag>
              </span>
              <!-- 접힌 상태에서 보상조건 태그 표시 -->
              <div v-if="isEventInfoCollapsed && rewardThresholdTags.length > 0" class="reward-tags-summary">
                <span class="reward-tags-label"><i class="fa fa-gift"></i> 보상조건:</span>
                <a-tooltip v-for="tag in rewardThresholdTags" :key="tag.key" :title="tag.fullLabel">
                  <a-tag :color="tag.color" size="small" :class="{ 'reward-inactive-tag': !tag.isActive }">
                    {{ tag.label }}
                  </a-tag>
                </a-tooltip>
              </div>
            </div>
          </a-col>
          <a-col>
            <a-button type="text" @click="isEventInfoCollapsed = !isEventInfoCollapsed" class="collapse-button">
              <template #icon>
                <i :class="isEventInfoCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'"></i>
              </template>
              {{ isEventInfoCollapsed ? '펼치기' : '접기' }}
            </a-button>
          </a-col>
        </a-row>
      </div>

      <!-- 접을 수 있는 이벤트 기본정보 콘텐츠 -->
      <div v-show="!isEventInfoCollapsed" class="event-info-content">
        <!-- 기본 정보 그리드 -->
        <a-row :gutter="[16, 12]" class="event-basic-info">
          <a-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">이벤트명</span>
              <span class="info-value">{{ currentEventData.eventTitle }}</span>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">주최자</span>
              <span class="info-value">
                {{ currentEventData.organizers?.map(o => o.organizerName).join(', ') || '-' }}
              </span>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">기간</span>
              <span class="info-value">
                {{ formatEventDate(currentEventData.startDate) }} ~ {{ formatEventDate(currentEventData.endDate) }}
              </span>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">시나리오</span>
              <a-tag :color="getScenarioTypeColor(currentEventData.scenarioType)">
                {{ getScenarioTypeLabel(currentEventData.scenarioType) }}
              </a-tag>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">대상 고객</span>
              <a-tag :color="getTargetCustomersColor(currentEventData.targetCustomers)">
                {{ getTargetCustomersLabel(currentEventData.targetCustomers) }}
              </a-tag>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">대상 범위</span>
              <a-tag :color="currentEventData.targetScope === 'ALL' ? 'blue' : currentEventData.targetScope === 'EXCLUDE' ? 'red' : 'orange'">
                {{ currentEventData.targetScope === 'ALL' ? '전체' : currentEventData.targetScope === 'EXCLUDE' ? '특정 병원 제외' : '특정 병원' }}
              </a-tag>
            </div>
          </a-col>
          <a-col v-if="currentEventData.targetHospitals?.length > 0" :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">{{ currentEventData.targetScope === 'EXCLUDE' ? '제외 병원' : '대상 병원' }}</span>
              <span class="info-value">
                {{ currentEventData.targetHospitals.map(h => h.hospitalName).join(', ') }}
              </span>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8" :lg="6">
            <div class="info-item">
              <span class="info-label">쿠폰 상태</span>
              <a-tag :color="currentEventData.isCouponDepleted ? 'red' : 'green'">
                {{ currentEventData.isCouponDepleted ? '소진됨' : '발급 가능' }}
              </a-tag>
            </div>
          </a-col>
          <a-col v-if="currentEventData.description" :xs="24">
            <div class="info-item">
              <span class="info-label">설명</span>
              <span class="info-value">{{ currentEventData.description }}</span>
            </div>
          </a-col>
        </a-row>

        <!-- 보상조건 서브섹션 -->
        <div v-if="currentEventData.rewardThresholds?.length > 0" class="reward-subsection">
          <div class="subsection-title">
            <i class="fa fa-gift"></i>
            보상조건
            <a-badge
              :count="currentEventData.rewardThresholds.filter(r => r.isActive).length"
              :number-style="{ backgroundColor: '#52c41a', fontSize: '12px' }"
              style="margin-left: 8px;"
            />
          </div>
          <a-row :gutter="[16, 16]">
            <a-col
              v-for="(reward, index) in currentEventData.rewardThresholds"
              :key="index"
              :xs="24" :sm="12" :md="8" :lg="6" :xl="6"
            >
              <a-card
                size="small"
                :class="['reward-card', { 'reward-inactive': !reward.isActive }]"
              >
                <template #title>
                  <div class="reward-card-title">
                    <a-tag :color="getConditionTypeColor(reward.conditionType)" size="small">
                      {{ getConditionTypeLabel(reward.conditionType) }}
                    </a-tag>
                    <a-tag v-if="!reward.isActive" color="default" size="small">비활성</a-tag>
                  </div>
                </template>
                <div class="reward-card-body">
                  <div class="reward-name">{{ reward.rewardName }}</div>
                  <div class="reward-description">{{ reward.rewardDescription }}</div>
                  <a-divider style="margin: 8px 0;" />
                  <div class="reward-details">
                    <div class="reward-detail-row">
                      <span class="detail-label">대상:</span>
                      <a-tag :color="getTargetAudienceColor(reward.targetAudience)" size="small">
                        {{ getTargetAudienceLabel(reward.targetAudience) }}
                      </a-tag>
                    </div>
                    <div class="reward-detail-row">
                      <span class="detail-label">조건:</span>
                      <span class="detail-value">{{ reward.thresholdValue }}회</span>
                    </div>
                  </div>
                </div>
              </a-card>
            </a-col>
          </a-row>
        </div>
      </div>
    </div>

    <!-- 성과분석 섹션 -->
    <div v-if="currentEventData" class="performance-analysis-section mb-4">
      <!-- 성과분석 헤더 (항상 보임) -->
      <div class="performance-analysis-header">
        <a-row :gutter="16" align="middle" justify="space-between">
          <a-col :flex="1">
            <div class="performance-analysis-header-left">
              <span class="performance-analysis-title">
                <i class="fa fa-chart-line"></i>
                성과분석
              </span>
              <!-- 접힌 상태에서 요약 정보 표시 -->
              <div v-if="isPerformanceAnalysisCollapsed" class="performance-summary-tags">
                <a-tag color="blue">
                  총예산: {{ currentEventData.totalBudget ? currentEventData.totalBudget.toLocaleString() + '원' : '-' }}
                </a-tag>
                <a-tag color="orange">
                  소진: {{ spentBudget.amount ? spentBudget.amount.toLocaleString() + '원' : '0원' }}
                </a-tag>
                <a-tag color="green">
                  잔액: {{ currentEventData.totalBudget ? (currentEventData.totalBudget - (spentBudget.amount || 0)).toLocaleString() + '원' : '-' }}
                </a-tag>
              </div>
            </div>
          </a-col>
          <a-col>
            <a-button type="text" @click="isPerformanceAnalysisCollapsed = !isPerformanceAnalysisCollapsed" class="collapse-button">
              <template #icon>
                <i :class="isPerformanceAnalysisCollapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'"></i>
              </template>
              {{ isPerformanceAnalysisCollapsed ? '펼치기' : '접기' }}
            </a-button>
          </a-col>
        </a-row>
      </div>

      <!-- 접을 수 있는 성과분석 콘텐츠 -->
      <div v-show="!isPerformanceAnalysisCollapsed" class="performance-analysis-content">
        <a-row :gutter="[24, 16]">
          <a-col :xs="24" :sm="12" :md="8">
            <div
              class="performance-stat-card expandable-card"
              :class="{ 'expanded': isTotalBudgetExpanded }"
              @click="isTotalBudgetExpanded = !isTotalBudgetExpanded"
            >
              <div class="stat-icon budget-icon">
                <i class="fa fa-wallet"></i>
              </div>
              <div class="stat-info">
                <div class="stat-header">
                  <span class="stat-label">총예산</span>
                  <i class="fa" :class="isTotalBudgetExpanded ? 'fa-chevron-up' : 'fa-chevron-down'" style="font-size: 12px; color: #999;"></i>
                </div>
                <span class="stat-value">{{ currentEventData.totalBudget ? currentEventData.totalBudget.toLocaleString() + '원' : '-' }}</span>

                <!-- 펼쳤을 때 회차별 예산 내역 -->
                <div v-if="isTotalBudgetExpanded && currentEventData.budgetRounds?.length" class="spent-detail" @click.stop>
                  <div v-for="(round, index) in currentEventData.budgetRounds" :key="index" class="detail-row">
                    <span class="detail-label">
                      <i class="fa fa-layer-group"></i> {{ round.roundNumber }}회차
                    </span>
                    <span class="detail-value">
                      {{ round.amount ? round.amount.toLocaleString() + '원' : '0원' }}
                    </span>
                  </div>
                </div>
                <div v-else-if="isTotalBudgetExpanded && (!currentEventData.budgetRounds || !currentEventData.budgetRounds.length)" class="spent-detail" @click.stop>
                  <div class="detail-row">
                    <span class="detail-label" style="color: #999;">
                      등록된 예산 회차가 없습니다
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8">
            <div
              class="performance-stat-card expandable-card"
              :class="{ 'expanded': isSpentBudgetExpanded }"
              @click="isSpentBudgetExpanded = !isSpentBudgetExpanded"
            >
              <div class="stat-icon spent-icon">
                <i class="fa fa-coins"></i>
              </div>
              <div class="stat-info">
                <div class="stat-header">
                  <span class="stat-label">소진금액</span>
                  <i class="fa" :class="isSpentBudgetExpanded ? 'fa-chevron-up' : 'fa-chevron-down'" style="font-size: 12px; color: #999;"></i>
                </div>
                <span class="stat-value">{{ spentBudget.amount ? spentBudget.amount.toLocaleString() + '원' : '0원' }}</span>

                <!-- 펼쳤을 때 상세 내역 -->
                <div v-if="isSpentBudgetExpanded" class="spent-detail" @click.stop>
                  <div class="detail-row">
                    <span class="detail-label">
                      <i class="fa fa-gift"></i> 쿠폰 발급
                    </span>
                    <span class="detail-value">
                      {{ spentBudget.couponAmount ? spentBudget.couponAmount.toLocaleString() + '원' : '0원' }}
                    </span>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">
                      <i class="fa fa-pencil"></i> 직접 입력
                    </span>
                    <span class="detail-value">
                      {{ spentBudget.expenseAmount ? spentBudget.expenseAmount.toLocaleString() + '원' : '0원' }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </a-col>
          <a-col :xs="24" :sm="12" :md="8">
            <div class="performance-stat-card">
              <div class="stat-icon remaining-icon">
                <i class="fa fa-piggy-bank"></i>
              </div>
              <div class="stat-info">
                <span class="stat-label">잔여예산</span>
                <span class="stat-value">{{
                  currentEventData.totalBudget
                    ? ((currentEventData.totalBudget - (spentBudget.amount || 0))).toLocaleString() + '원'
                    : '-'
                }}</span>
              </div>
            </div>
          </a-col>
        </a-row>

        <!-- 예산 소진율 프로그레스 바 -->
        <div v-if="currentEventData.totalBudget" class="budget-progress-section mt-4">
          <div class="progress-label">
            <span>예산 소진율</span>
            <span class="progress-percentage">
              {{ Math.round(((spentBudget.amount || 0) / currentEventData.totalBudget) * 100) }}%
            </span>
          </div>
          <a-progress
            :percent="Math.round(((spentBudget.amount || 0) / currentEventData.totalBudget) * 100)"
            :stroke-color="{ '0%': '#52c41a', '100%': '#faad14' }"
            status="active"
          />
        </div>
      </div>
    </div>

    <!-- 세그먼트 동기화 진행 배너 -->
    <div v-if="segmentSyncStatus === 'processing'" class="sync-progress-banner">
      <a-alert type="warning" show-icon :closable="false">
        <template #message>
          <div class="sync-banner-content">
            <span class="sync-title">
              <i class="fa fa-users fa-spin"></i>
              고객 세그멘테이션 동기화 진행 중
            </span>
            <span class="sync-stats">
              {{ segmentSyncProgress.syncedCount || 0 }} / {{ segmentSyncProgress.totalCount || 0 }}명 처리
              <span v-if="segmentElapsedTime" class="sync-time">({{ segmentElapsedTime }})</span>
            </span>
          </div>
        </template>
        <template #description>
          <div class="sync-details">
            <a-progress
              :percent="segmentSyncProgress.percentage || 0"
              :stroke-color="{ '0%': '#faad14', '100%': '#87d068' }"
              status="active"
            />
            <a-row :gutter="16" class="mt-2">
              <a-col :span="8">
                <span>✅ 동기화됨: {{ segmentSyncProgress.syncedCount || 0 }}명</span>
              </a-col>
              <a-col :span="8">
                <span>⏳ 남은 수: {{ segmentSyncProgress.remainingCount || 0 }}명</span>
              </a-col>
              <a-col :span="8">
                <span>현재 진행률: {{ segmentSyncProgress.percentage || 0 }}%</span>
              </a-col>
            </a-row>
          </div>
        </template>
      </a-alert>
    </div>

    <!-- 참여자 테이블 -->
    <div class="table-section">
      <div class="table-header mb-3">
        <a-row :gutter="[12, 12]" align="middle" justify="space-between">
          <!-- 왼쪽: 컨텍스트 액션 (선택된 데이터에 대한 작업) -->
          <a-col :xs="24" :sm="24" :md="14" :lg="12" :xl="12">
            <a-space wrap class="table-action-space">
              <a-button
                v-if="selectedEvent"
                size="small"
                @click="showResourceDrawer = true"
              >
                <template #icon><i class="fa fa-folder-open"></i></template>
                <span class="btn-text-hide-mobile">자료실</span>
              </a-button>
              <a-divider v-if="selectedRows.length > 0" type="vertical" class="action-divider" />
              <span v-if="selectedRows.length > 0">
                <a-tag color="blue">{{ selectedRows.length }}명 선택됨</a-tag>
              </span>
              <a-button
                v-if="selectedRows.length > 0"
                type="primary"
                size="small"
                @click="handleSendMessage"
                class="yellow-button"
              >
                <template #icon><i class="fa fa-comment"></i></template>
                <span class="btn-text-hide-mobile">메시지 발송</span>
              </a-button>
              <a-button
                v-if="selectedRows.length > 0"
                type="primary"
                size="small"
                @click="handleSendWinnerNotification"
                class="yellow-button"
              >
                <template #icon><i class="fa fa-trophy"></i></template>
                <span class="btn-text-hide-mobile">당첨알림</span>
              </a-button>
              <a-button
                v-if="selectedRows.length > 0 && hasCertificateHolders"
                type="primary"
                size="small"
                @click="handleResendCertificateNotification"
                class="yellow-button"
              >
                <template #icon><i class="fa fa-refresh"></i></template>
                <span class="btn-text-hide-mobile">재발송</span>
              </a-button>
              <a-button
                v-if="selectedRows.length > 0"
                danger
                size="small"
                @click="handleDeleteAttendees"
              >
                <template #icon><i class="fa fa-trash"></i></template>
                <span class="btn-text-hide-mobile">삭제</span>
              </a-button>
            </a-space>
          </a-col>
          <!-- 오른쪽: 글로벌 액션 (네비게이션 + 도구) -->
          <a-col :xs="24" :sm="24" :md="10" :lg="12" :xl="12" class="table-header-right">
            <a-space wrap class="global-action-space">
              <!-- 네비게이션 그룹 -->
              <a-button
                type="default"
                size="small"
                @click="goToEventList"
              >
                <template #icon><i class="fa fa-list"></i></template>
                <span class="btn-text-hide-mobile">이벤트 목록</span>
              </a-button>
              <a-button
                v-if="selectedEvent"
                type="primary"
                size="small"
                @click="goToEditEvent"
                class="yellow-button"
              >
                <template #icon><i class="fa fa-edit"></i></template>
                <span class="btn-text-hide-mobile">이벤트 수정</span>
              </a-button>
              <a-divider v-if="selectedEvent" type="vertical" class="action-divider" />
              <!-- 데이터 관리 그룹 -->
              <a-button
                v-if="selectedEvent"
                type="primary"
                size="small"
                :loading="segmentSyncStatus === 'processing'"
                @click="handleSegmentSync"
                class="yellow-button"
              >
                <template #icon v-if="segmentSyncStatus !== 'processing'">
                  <i v-if="segmentSyncStatus === 'completed'" class="fa fa-check"></i>
                  <i v-else-if="segmentSyncStatus === 'failed'" class="fa fa-exclamation-triangle"></i>
                  <i v-else class="fa fa-users"></i>
                </template>
                <span class="btn-text-hide-mobile">{{ segmentSyncButtonText }}</span>
                <span class="btn-text-show-mobile">동기화</span>
              </a-button>
              <!-- 더보기 드롭다운 -->
              <a-dropdown v-if="selectedEvent" :trigger="['click']">
                <a-button size="small">
                  <template #icon><i class="fa fa-ellipsis-v"></i></template>
                  <span class="btn-text-hide-mobile">더보기</span>
                </a-button>
                <template #overlay>
                  <a-menu class="more-actions-menu">
                    <a-menu-item-group title="데이터 관리">
                      <a-menu-item key="thermage" @click="thermageMigrationDrawerVisible = true">
                        <i class="fa fa-exchange"></i>
                        써마지 마이그레이션
                      </a-menu-item>
                      <a-menu-item key="organizer-cert" @click="organizerCertMigrationDrawerVisible = true">
                        <i class="fa fa-certificate"></i>
                        인증서 수신자 마이그레이션
                      </a-menu-item>
                    </a-menu-item-group>
                    <a-menu-divider />
                    <a-menu-item-group title="개발자 도구">
                      <a-menu-item key="loadtest" @click="showLoadTestModal" class="dev-tool-item">
                        <i class="fa fa-bolt"></i>
                        부하테스트
                      </a-menu-item>
                    </a-menu-item-group>
                  </a-menu>
                </template>
              </a-dropdown>
            </a-space>
          </a-col>
        </a-row>
      </div>

      <!-- 모바일 카드 리스트 -->
      <div v-if="isMobile" class="mobile-card-list">
        <div class="mobile-list-header">
          <a-checkbox
            :checked="selectedRowKeys.length > 0 && selectedRowKeys.length === filteredData.length"
            :indeterminate="selectedRowKeys.length > 0 && selectedRowKeys.length < filteredData.length"
            @change="handleSelectAll"
          >
            전체 선택
          </a-checkbox>
          <span class="total-count">총 {{ pagination.total?.toLocaleString() || 0 }}명</span>
        </div>

        <a-spin :spinning="loading">
          <div v-if="filteredData.length === 0" class="mobile-empty">
            <a-empty description="데이터가 없습니다" />
          </div>

          <div v-else class="mobile-cards-container">
            <div
              v-for="(record, index) in filteredData"
              :key="record._id || index"
              class="participant-card"
              :class="{ selected: selectedRowKeysSet.has(record._id) }"
            >
              <div class="card-header">
                <a-checkbox
                  :checked="selectedRowKeysSet.has(record._id)"
                  @change="(e) => handleCardSelect(record, e.target.checked)"
                />
                <span class="card-index">{{ (pagination.current - 1) * pagination.pageSize + index + 1 }}</span>
                <div class="card-segment" v-if="record.segment">
                  <a-tag :color="getSegmentPrimaryColor(record.segment.primary)" size="small">
                    {{ getSegmentPrimaryLabel(record.segment.primary) }}
                  </a-tag>
                </div>
              </div>

              <div class="card-body" @click="viewDetail(record)">
                <div class="card-row">
                  <span class="card-label">이름</span>
                  <span class="card-value">{{ record.customerName || '-' }}</span>
                </div>
                <div class="card-row">
                  <span class="card-label">연락처</span>
                  <span class="card-value">{{ record.customerMobile || '-' }}</span>
                </div>
                <div class="card-row">
                  <span class="card-label">참여일</span>
                  <span class="card-value">{{ formatDate(record.participatedAt) }}</span>
                </div>
                <div class="card-row">
                  <span class="card-label">진행상태</span>
                  <div class="card-progress">
                    <span class="mini-indicator" :class="{ active: hasParticipated(record) }">참여</span>
                    <span class="mini-indicator" :class="{ active: record.reviewWritten }">리뷰</span>
                    <span class="mini-indicator" :class="{ active: record.sharedAt }">공유</span>
                    <span class="mini-indicator" :class="{ active: record.couponReceived }">쿠폰</span>
                  </div>
                </div>
              </div>

              <div class="card-footer">
                <a-button size="small" @click="viewDetail(record)">
                  <i class="fa fa-eye"></i> 상세
                </a-button>
                <a-button size="small" @click="sendIndividualMessage(record)">
                  <i class="fa fa-envelope"></i> 메시지
                </a-button>
              </div>
            </div>
          </div>
        </a-spin>

        <!-- 모바일 페이지네이션 -->
        <div class="mobile-pagination">
          <a-pagination
            v-model:current="pagination.current"
            :total="pagination.total"
            :page-size="pagination.pageSize"
            :show-size-changer="false"
            :show-quick-jumper="false"
            simple
            @change="handleMobilePageChange"
          />
        </div>
      </div>

      <!-- PC/태블릿 테이블 -->
      <a-table
        v-else
        :columns="columns"
        :data-source="filteredData"
        :row-selection="{
          selectedRowKeys: selectedRowKeys,
          onChange: onSelectChange,
        }"
        :pagination="pagination"
        @change="handleTableChange"
        :loading="loading"
        size="small"
        :scroll="{ x: 1400 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'segment'">
            <div v-if="record.segment" style="font-size: 12px; line-height: 1.5;">
              <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">
                <a-tag :color="getSegmentPrimaryColor(record.segment.primary)" style="margin: 0;">
                  {{ getSegmentPrimaryLabel(record.segment.primary) }}
                </a-tag>
                <a-badge
                  v-if="record.certificates && record.certificates.length > 0"
                  :count="record.certificates.length"
                  :number-style="{ backgroundColor: '#52c41a', fontSize: '10px' }"
                  :title="`${record.certificates.length}개 인증서 보유`"
                >
                  <i class="fa fa-certificate" style="font-size: 14px; color: #52c41a;"></i>
                </a-badge>
              </div>
              <div v-if="record.segment.secondary && record.segment.secondary.length > 0" style="display: flex; flex-wrap: wrap; gap: 4px;">
                <a-tag
                  v-for="(sec, idx) in record.segment.secondary"
                  :key="idx"
                  size="small"
                  :color="getSegmentSecondaryColor(sec)"
                  style="margin: 0; font-size: 11px;"
                >
                  {{ getSegmentSecondaryLabel(sec) }}
                </a-tag>
              </div>
            </div>
            <span v-else style="color: #ccc;">-</span>
          </template>

          <template v-if="column.key === 'name'">
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <span style="font-weight: 500;">
                <span style="font-size: 11px; color: #999; margin-right: 4px;">참여자:</span>
                {{ record.customerName || '-' }}
              </span>
              <span style="font-size: 12px; color: #999;">
                <span style="font-size: 11px; margin-right: 4px;">고객:</span>
                {{ record.customerInfo?.name || '-' }}
              </span>
            </div>
          </template>

          <template v-if="column.key === 'mobile'">
            <div style="display: flex; flex-direction: column; gap: 4px;">
              <span style="font-weight: 500;">
                <span style="font-size: 11px; color: #999; margin-right: 4px;">참여자:</span>
                {{ record.customerMobile || '-' }}
              </span>
              <span style="font-size: 12px; color: #999;">
                <span style="font-size: 11px; margin-right: 4px;">고객:</span>
                {{ record.customerInfo?.mobile || '-' }}
              </span>
            </div>
          </template>

          <template v-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.status)">
              {{ getStatusText(record.status) }}
            </a-tag>
          </template>
          
          <template v-if="column.key === 'marketingConsent'">
            <a-tag v-if="record.marketingConsent === 'agreed'" color="green">
              동의
            </a-tag>
            <a-tag v-else-if="record.marketingConsent === 'revoked'" color="red">
              미동의
            </a-tag>
            <span v-else>-</span>
          </template>

          <template v-if="column.key === 'deleteYn'">
            <a-tag v-if="record.deleteYn === true || record.deleteYn === 'Y'" color="red">
              삭제
            </a-tag>
            <a-tag v-else color="green">
              정상
            </a-tag>
          </template>
          
          <template v-if="column.key === 'progress'">
            <div class="progress-indicators">
              <a-tooltip title="이벤트 참여">
                <span class="indicator" :class="{ active: hasParticipated(record) }">
                  <i class="fa fa-user"></i>
                </span>
              </a-tooltip>
              <span class="indicator-line" :class="{ active: record.reviewWritten }"></span>
              <a-tooltip :title="`리뷰 작성${record.reviewCount > 0 ? ` (${record.reviewCount}개)` : ''}`">
                <span class="indicator" :class="{ active: record.reviewWritten }">
                  <i class="fa fa-star"></i>
                  <span v-if="record.reviewCount > 1" class="share-count">{{ record.reviewCount }}</span>
                </span>
              </a-tooltip>
              <span class="indicator-line" :class="{ active: record.sharedAt }"></span>
              <a-popover
                :title="`공유하기${record.shareCount > 0 ? ` (${record.shareCount}회)` : ''}`"
                trigger="hover"
                placement="top"
              >
                <template #content>
                  <div v-if="record.shareCount > 0 || (record.shareChatList && record.shareChatList.length > 0) || (record.referredUsers && record.referredUsers.length > 0)" style="max-width: 300px; max-height: 200px; overflow-y: auto;">
                    <!-- 공유 채팅 목록 -->
                    <div v-if="record.shareChatList && record.shareChatList.length > 0">
                      <div style="font-weight: 600; margin-bottom: 8px; color: #1890ff;">공유 대상</div>
                      <div v-for="(chat, idx) in record.shareChatList" :key="'chat-' + idx" style="margin-bottom: 4px; padding: 4px; background: #f5f5f5; border-radius: 4px;">
                        <div>{{ getChatTypeName(chat.chatType) }}</div>
                        <div style="font-size: 11px; color: #999;">{{ formatDate(chat.sharedAt || chat.createdAt) }}</div>
                      </div>
                    </div>
                    <!-- 추천으로 참여한 사용자 -->
                    <div v-if="record.referredUsers && record.referredUsers.length > 0" :style="record.shareChatList && record.shareChatList.length > 0 ? 'margin-top: 12px; padding-top: 8px; border-top: 1px solid #f0f0f0;' : ''">
                      <div style="font-weight: 600; margin-bottom: 8px; color: #52c41a;">추천으로 참여 ({{ record.referredUsers.length }}명)</div>
                      <div v-for="(user, idx) in record.referredUsers" :key="'user-' + idx" style="margin-bottom: 4px; padding: 4px; background: #f6ffed; border-radius: 4px;">
                        {{ user.name || user.customerName || '알 수 없음' }}
                      </div>
                    </div>
                    <!-- 공유 횟수만 있고 상세 정보가 없는 경우 -->
                    <div v-if="record.shareCount > 0 && (!record.shareChatList || record.shareChatList.length === 0) && (!record.referredUsers || record.referredUsers.length === 0)">
                      <span style="color: #666;">{{ record.shareCount }}회 공유됨 (상세 정보 없음)</span>
                    </div>
                  </div>
                  <div v-else style="color: #999;">공유 이력 없음</div>
                </template>
                <span class="indicator" :class="{ active: record.sharedAt }">
                  <i class="fa fa-share-alt"></i>
                  <span v-if="record.shareCount > 1" class="share-count">{{ record.shareCount }}</span>
                </span>
              </a-popover>
              <span class="indicator-line" :class="{
                active: record.couponReceived,
                'coupon-pending': record.couponInfo?.length > 0 && !record.couponReceived
              }"></span>
              <a-tooltip :title="getCouponTooltip(record)">
                <span class="indicator" :class="{
                  active: record.couponReceived,
                  'coupon-pending': record.couponInfo?.length > 0 && !record.couponReceived
                }">
                  <i class="fa fa-ticket"></i>
                  <span v-if="record.couponInfo && record.couponInfo.length > 1" class="share-count">{{ record.couponInfo.length }}</span>
                </span>
              </a-tooltip>
              <span class="indicator-line" :class="{ active: record.loggedIn }"></span>
              <a-tooltip title="로그인">
                <span class="indicator" :class="{ active: record.loggedIn }">
                  <i class="fa fa-sign-in"></i>
                </span>
              </a-tooltip>
            </div>
          </template>
          
          <template v-if="column.key === 'eventMetadata'">
            <div v-if="record.eventMetadata && Array.isArray(record.eventMetadata) && record.eventMetadata.length > 0">
              <div style="font-size: 12px; line-height: 1.4;">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 4px;">
                  <div style="font-weight: 600; color: #1890ff;">
                    총 {{ record.eventMetadata.length }}개 이벤트 참여
                  </div>
                  <a-button
                    v-if="record.eventMetadata.length > 2"
                    size="small"
                    type="link"
                    @click="toggleEventExpand(record._id)"
                    style="padding: 0; height: auto; font-size: 11px;"
                  >
                    {{ expandedEvents[record._id] ? '접기' : '펼쳐보기' }}
                    <i :class="expandedEvents[record._id] ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="margin-left: 4px;"></i>
                  </a-button>
                </div>
                <div
                  v-for="(event, index) in getDisplayedEvents(record)"
                  :key="index"
                  style="margin-bottom: 6px; padding: 4px; background: #f5f5f5; border-radius: 4px; cursor: pointer; transition: background 0.3s;"
                  @click="handleSingleEventClick(record, event)"
                  @mouseenter="$event.currentTarget.style.background = '#e6f7ff'"
                  @mouseleave="$event.currentTarget.style.background = '#f5f5f5'"
                >
                  <div style="font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                    {{ event.eventTitle || '제목 없음' }}
                  </div>
                  <div style="display: flex; gap: 8px; margin-top: 2px; flex-wrap: wrap;">
                    <span style="color: #666;">{{ formatDate(event.participatedAt).split(' ')[0] }}</span>
                    <span v-if="event.reviewWritten" style="color: #52c41a;">✅리뷰</span>
                    <span v-else style="color: #999;">❌리뷰</span>
                    <span v-if="event.surveyCompleted" style="color: #52c41a;">✅설문</span>
                    <span v-else style="color: #999;">❌설문</span>
                    <!-- couponInfo 기반 쿠폰 상태 표시 -->
                    <span v-if="record.couponInfo && record.couponInfo.length > 0">
                      <!-- 쿠폰 발급된 상태 (COUPON_ISSUED) -->
                      <span v-if="record.couponInfo.some(c => c.status === 'COUPON_ISSUED')" style="color: #52c41a;">
                        🎫쿠폰{{ record.couponInfo.filter(c => c.status === 'COUPON_ISSUED').length }}개(발급완료)
                      </span>
                      <!-- 당첨 선정된 상태 (winner_selected) -->
                      <span v-else-if="record.couponInfo.some(c => c.status === 'winner_selected')" style="color: #faad14;">
                        🎫쿠폰{{ record.couponInfo.filter(c => c.status === 'winner_selected').length }}개(발급권한)
                      </span>
                      <!-- 기타 상태 -->
                      <span v-else style="color: #999;">
                        🎫쿠폰{{ record.couponInfo.length }}개
                      </span>
                    </span>
                    <!-- couponInfo 배열이 비어있거나 없는 경우 -->
                    <span v-else style="color: #999;">🎫쿠폰0개</span>
                  </div>
                </div>
              </div>
            </div>
            <span v-else style="color: #999;">-</span>
          </template>

          <template v-if="column.key === 'hospital'">
            <div v-if="record.hospitalMetadatas && record.hospitalMetadatas.length > 0" class="hospital-cell">
              <div class="hospital-header">
                <div class="hospital-count">
                  총 {{ record.hospitalMetadatas.length }}개 병원
                </div>
                <a-button
                  v-if="record.hospitalMetadatas.length > 2"
                  size="small"
                  type="link"
                  @click="toggleHospitalExpand(record._id)"
                  class="expand-button"
                >
                  {{ expandedHospitals[record._id] ? '접기' : '펼쳐보기' }}
                  <i :class="expandedHospitals[record._id] ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="margin-left: 4px;"></i>
                </a-button>
              </div>
              <div
                v-for="(hospital, index) in getDisplayedHospitals(record)"
                :key="hospital.companyId || index"
                class="hospital-item"
              >
                <div class="hospital-name">
                  {{ hospital.name || '-' }}
                </div>
                <div v-if="hospital.lastIssuedAt" class="hospital-date">
                  최근 발행: {{ formatDate(hospital.lastIssuedAt).split(' ')[0] }}
                </div>
              </div>
              <div v-if="!expandedHospitals[record._id] && record.hospitalMetadatas.length > 2" class="hospital-more">
                외 {{ record.hospitalMetadatas.length - 2 }}곳 더 있음
              </div>
            </div>
            <span v-else style="color: #999;">-</span>
          </template>

          <template v-if="column.key === 'participatedAt'">
            {{ formatDate(record.participatedAt) }}
          </template>
          
          <template v-if="column.key === 'copyActions'">
            <a-dropdown>
              <a-button size="small">
                복사/링크 <i class="fa fa-caret-down"></i>
              </a-button>
              <template #overlay>
                <a-menu>
                  <a-menu-item-group title="아이디 복사">
                    <a-menu-item @click="copyCustomerId(record)" :disabled="!record.customerId">
                      <i class="fa fa-user" style="margin-right: 8px;"></i>
                      고객 아이디
                    </a-menu-item>
                    <a-menu-item @click="copyEventAttendeesId(record)">
                      <i class="fa fa-calendar" style="margin-right: 8px;"></i>
                      참여자 아이디
                    </a-menu-item>
                    <a-menu-item @click="copyDeviceId(record)" :disabled="!record.deviceId">
                      <i class="fa fa-mobile" style="margin-right: 8px;"></i>
                      deviceId (legacy)
                    </a-menu-item>
                    <a-menu-item @click="copyReferrerId(record)" :disabled="!record.referrerId">
                      <i class="fa fa-link" style="margin-right: 8px;"></i>
                      referrerId
                    </a-menu-item>
                    <a-menu-item @click="copyReferrerId2(record)" :disabled="!record.referrerId2">
                      <i class="fa fa-link" style="margin-right: 8px;"></i>
                      referrerId2
                    </a-menu-item>
                  </a-menu-item-group>
                  <a-menu-divider />
                  <a-menu-item-group title="링크 생성">
                    <a-menu-item @click="copyPatientLink(record)">
                      <i class="fa fa-user-injured" style="margin-right: 8px;"></i>
                      환자 링크
                    </a-menu-item>
                    <a-menu-item @click="copyFriendLink(record)">
                      <i class="fa fa-users" style="margin-right: 8px;"></i>
                      친구 링크
                    </a-menu-item>
                    <a-menu-item @click="copyReviewLink(record)" :disabled="!record.certificates || record.certificates.length === 0">
                      <i class="fa fa-star" style="margin-right: 8px;"></i>
                      후기 링크
                    </a-menu-item>
                  </a-menu-item-group>
                </a-menu>
              </template>
            </a-dropdown>
          </template>

          <template v-if="column.key === 'actions'">
            <a-dropdown>
              <a-button size="small">
                액션 <i class="fa fa-caret-down"></i>
              </a-button>
              <template #overlay>
                <a-menu>
                  <a-menu-item @click="sendIndividualMessage(record)">
                    <i class="fa fa-envelope"></i> 메시지 발송
                  </a-menu-item>
                  <a-menu-item @click="viewDetail(record)">
                    <i class="fa fa-eye"></i> 상세 보기
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item @click="resetParticipationInfo(record)">
                    <i class="fa fa-refresh"></i> 참여정보 초기화
                    <a-tooltip placement="right">
                      <template #title>
                        <div style="max-width: 300px;">
                          <div style="font-weight: 600; margin-bottom: 8px;">초기화되는 정보:</div>
                          <div style="margin-bottom: 4px;">• 공유 관련: 공유 횟수, 공유 이벤트, 채팅방 목록</div>
                          <div style="margin-bottom: 4px;">• 활동/점수: 활동 이력, 총 점수</div>
                          <div style="margin-bottom: 4px;">• 입장 정보: 입장 여부, 입장 시간</div>
                          <div style="margin-bottom: 8px;">• 쿠폰 정보: 발급된 쿠폰</div>
                          <div style="font-weight: 600; margin-bottom: 4px;">유지되는 정보:</div>
                          <div>• 기본 참석자 정보, 메시지 상태, 생성/수정 시간</div>
                        </div>
                      </template>
                      <i class="fa fa-question-circle" style="margin-left: 4px; color: #999;"></i>
                    </a-tooltip>
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item @click="showDuplicateTestModal(record)" style="color: #cf1322;">
                    <i class="fa fa-clone"></i> 중복응모 테스트
                    <a-tooltip placement="right">
                      <template #title>
                        <div style="max-width: 280px;">
                          <div style="font-weight: 600; margin-bottom: 8px;">원자성 테스트</div>
                          <div>이 참여자의 전화번호로 동시에 여러 요청을 보내 findOneAndUpdate 원자성을 검증합니다.</div>
                          <div style="margin-top: 8px; color: #faad14;">쿠폰이 1개만 발급되어야 정상입니다.</div>
                        </div>
                      </template>
                      <i class="fa fa-question-circle" style="margin-left: 4px; color: #999;"></i>
                    </a-tooltip>
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </template>
        </template>
      </a-table>
    </div>
    
    <!-- 참여자 상세 정보 Drawer -->
    <ParticipantDetailDrawer
      :visible="detailDrawerVisible"
      :participant="selectedParticipant"
      @close="detailDrawerVisible = false"
      @coupon-deleted="handleCouponDeleted"
      @coupon-resent="handleCouponResent"
      @refresh="handleDrawerRefresh"
    />
    
    <!-- 메시지 발송 Drawer -->
    <MessageSendDrawer
      :visible="messageDrawerVisible"
      :recipients="messageRecipients"
      :event-id="selectedEvent"
      :default-template-code="currentEventData?.alimtalkTemplateCode || ''"
      @close="messageDrawerVisible = false"
      @send="handleMessageSent"
    />

    <!-- 참여자 삭제 Drawer -->
    <AttendeeDeleteDrawer
      :visible="deleteDrawerVisible"
      :attendee-ids="selectedRowKeys"
      :attendees="selectedRows"
      :api-instance="apiInstance"
      @close="deleteDrawerVisible = false"
      @delete-success="handleDeleteSuccess"
    />

    <!-- 자료실 Drawer -->
    <EventResourceDrawer
      v-model:visible="showResourceDrawer"
      :event-id="selectedEvent"
      :api="apiInstance"
    />

    <!-- 써마지 마이그레이션 Drawer -->
    <ThermageMigrationDrawer
      :visible="thermageMigrationDrawerVisible"
      :target-event-id="selectedEvent"
      :event-title="currentEventData?.eventTitle || ''"
      :api-instance="apiInstance"
      @close="thermageMigrationDrawerVisible = false"
      @migration-complete="handleThermageMigrationComplete"
    />

    <!-- 주최자 인증서 수신자 마이그레이션 Drawer -->
    <OrganizerCertificateMigrationDrawer
      :visible="organizerCertMigrationDrawerVisible"
      :target-event-id="selectedEvent"
      :event-title="currentEventData?.eventTitle || ''"
      :api-instance="apiInstance"
      @close="organizerCertMigrationDrawerVisible = false"
      @migration-complete="handleThermageMigrationComplete"
    />

    <!-- 부하테스트 모달 -->
    <a-modal
      v-model:visible="loadTestModalVisible"
      title="API 부하테스트"
      :footer="null"
      width="500px"
    >
      <div class="load-test-modal">
        <a-alert
          message="주의: 테스트 환경에서만 사용하세요"
          description="실제 이벤트 참여 데이터가 생성됩니다. Rate Limiting (분당 3회/전화번호) 테스트 목적입니다."
          type="warning"
          show-icon
          style="margin-bottom: 16px;"
        />

        <a-form layout="vertical">
          <a-form-item label="초당 요청 수">
            <a-input-number
              v-model:value="loadTestConfig.requestsPerSecond"
              :min="1"
              :max="50"
              style="width: 100%;"
            />
            <div class="form-hint">1~50 사이 값 (권장: 5~10)</div>
          </a-form-item>

          <a-form-item label="총 요청 수">
            <a-input-number
              v-model:value="loadTestConfig.totalRequests"
              :min="1"
              :max="100"
              style="width: 100%;"
            />
            <div class="form-hint">1~100 사이 값 (권장: 10~30)</div>
          </a-form-item>

          <a-form-item label="전화번호 모드">
            <a-radio-group v-model:value="loadTestConfig.phoneMode">
              <a-radio value="random">
                <span>랜덤 생성</span>
                <span class="radio-hint">(서버 부하 테스트)</span>
              </a-radio>
              <a-radio value="fixed">
                <span>고정 번호</span>
                <span class="radio-hint">(Rate Limit 테스트)</span>
              </a-radio>
            </a-radio-group>
          </a-form-item>

          <a-form-item v-if="loadTestConfig.phoneMode === 'fixed'" label="테스트 전화번호">
            <a-input
              v-model:value="loadTestConfig.fixedPhone"
              placeholder="01012345678"
              style="width: 100%;"
            />
            <div class="form-hint">이 번호로 모든 요청을 보냅니다</div>
          </a-form-item>

        </a-form>

        <!-- 테스트 계획 & 예상 결과 시뮬레이션 -->
        <div class="test-calculation-summary">
          <div class="calculation-header">테스트 계획</div>
          <div class="calculation-detail">
            <span class="calc-item">
              <span class="calc-label">초당</span>
              <span class="calc-value">{{ loadTestConfig.requestsPerSecond }}개</span>
            </span>
            <span class="calc-operator">×</span>
            <span class="calc-item">
              <span class="calc-label">소요시간</span>
              <span class="calc-value">{{ Math.ceil(loadTestConfig.totalRequests / loadTestConfig.requestsPerSecond) }}초</span>
            </span>
            <span class="calc-operator">=</span>
            <span class="calc-item calc-result">
              <span class="calc-label">총</span>
              <span class="calc-value">{{ loadTestConfig.totalRequests }}개</span>
            </span>
          </div>
          <div class="calculation-description">
            {{ Math.ceil(loadTestConfig.totalRequests / loadTestConfig.requestsPerSecond) }}초 동안
            매초 {{ loadTestConfig.requestsPerSecond }}개씩
            총 {{ loadTestConfig.totalRequests }}개의 요청을 보냅니다.
          </div>

          <!-- 예상 결과 시뮬레이션 -->
          <div class="expected-results">
            <div class="expected-header">
              <i class="fa fa-calculator"></i> 예상 결과
            </div>
            <div class="expected-detail">
              <span class="expected-item success">
                <i class="fa fa-check-circle"></i>
                성공: {{ loadTestExpected.success }}개
              </span>
              <span class="expected-item rate-limited">
                <i class="fa fa-ban"></i>
                429: {{ loadTestExpected.rateLimited }}개
              </span>
            </div>
            <div class="expected-description">
              {{ loadTestExpected.description }}
            </div>
          </div>
        </div>

        <!-- 실행 결과 표시 (예상 vs 실제 비교) -->
        <div v-if="loadTestRunning || loadTestResults.success + loadTestResults.failed + loadTestResults.rateLimited > 0" class="load-test-results">
          <a-divider>테스트 결과 (예상 vs 실제)</a-divider>

          <!-- 비교 테이블 -->
          <div class="comparison-table">
            <div class="comparison-row header">
              <span class="comparison-cell">항목</span>
              <span class="comparison-cell">예상</span>
              <span class="comparison-cell">실제</span>
              <span class="comparison-cell">일치</span>
            </div>
            <div class="comparison-row">
              <span class="comparison-cell label">
                <i class="fa fa-check-circle" style="color: #3f8600;"></i> 성공
              </span>
              <span class="comparison-cell expected">{{ loadTestExpected.success }}</span>
              <span class="comparison-cell actual">{{ loadTestResults.success }}</span>
              <span class="comparison-cell match">
                <i v-if="loadTestResults.success === loadTestExpected.success" class="fa fa-check" style="color: #3f8600;"></i>
                <i v-else class="fa fa-times" style="color: #cf1322;"></i>
              </span>
            </div>
            <div class="comparison-row">
              <span class="comparison-cell label">
                <i class="fa fa-ban" style="color: #cf1322;"></i> 429
              </span>
              <span class="comparison-cell expected">{{ loadTestExpected.rateLimited }}</span>
              <span class="comparison-cell actual">{{ loadTestResults.rateLimited }}</span>
              <span class="comparison-cell match">
                <i v-if="loadTestResults.rateLimited === loadTestExpected.rateLimited" class="fa fa-check" style="color: #3f8600;"></i>
                <i v-else class="fa fa-times" style="color: #cf1322;"></i>
              </span>
            </div>
            <div class="comparison-row">
              <span class="comparison-cell label">
                <i class="fa fa-times-circle" style="color: #999;"></i> 기타 실패
              </span>
              <span class="comparison-cell expected">0</span>
              <span class="comparison-cell actual">{{ loadTestResults.failed }}</span>
              <span class="comparison-cell match">
                <i v-if="loadTestResults.failed === 0" class="fa fa-check" style="color: #3f8600;"></i>
                <i v-else class="fa fa-times" style="color: #cf1322;"></i>
              </span>
            </div>
          </div>

          <!-- 종합 판정 -->
          <div v-if="!loadTestRunning" class="test-verdict">
            <a-alert
              v-if="loadTestResults.success === loadTestExpected.success && loadTestResults.rateLimited === loadTestExpected.rateLimited && loadTestResults.failed === 0"
              type="success"
              show-icon
              message="테스트 통과"
              description="실제 결과가 예상 결과와 일치합니다. Rate Limiting이 정상 동작합니다."
            />
            <a-alert
              v-else
              type="warning"
              show-icon
              message="결과 불일치"
              :description="`예상과 다른 결과가 나왔습니다. 성공: ${loadTestResults.success}/${loadTestExpected.success}, 429: ${loadTestResults.rateLimited}/${loadTestExpected.rateLimited}, 실패: ${loadTestResults.failed}`"
            />
          </div>

          <a-progress
            v-if="loadTestRunning"
            :percent="Math.round((loadTestResults.success + loadTestResults.failed + loadTestResults.rateLimited) / loadTestConfig.totalRequests * 100)"
            status="active"
            style="margin-top: 16px;"
          />

          <!-- Raw 응답 보기 -->
          <div v-if="!loadTestRunning && loadTestResults.rawResponses.length > 0" class="raw-responses-section">
            <a-button
              type="link"
              size="small"
              @click="showRawResponses = !showRawResponses"
              style="padding: 0; margin-top: 12px;"
            >
              <i :class="showRawResponses ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" style="margin-right: 4px;"></i>
              {{ showRawResponses ? 'Raw 응답 숨기기' : 'Raw 응답 보기' }} ({{ loadTestResults.rawResponses.length }}개)
            </a-button>

            <div v-if="showRawResponses" class="raw-responses-list">
              <div
                v-for="item in loadTestResults.rawResponses"
                :key="item.index"
                class="raw-response-item"
                :class="{
                  'success': item.status >= 200 && item.status < 300,
                  'rate-limited': item.status === 429,
                  'failed': item.status >= 400 && item.status !== 429
                }"
              >
                <div class="response-header">
                  <span class="response-index">#{{ item.index }}</span>
                  <span class="response-status" :class="'status-' + item.status">{{ item.status }}</span>
                  <span class="response-phone">{{ item.phone }}</span>
                </div>
                <pre class="response-body">{{ JSON.stringify(item.response, null, 2) }}</pre>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer" style="margin-top: 24px; text-align: right;">
          <a-button @click="loadTestModalVisible = false" style="margin-right: 8px;">
            닫기
          </a-button>
          <a-button
            type="primary"
            danger
            :loading="loadTestRunning"
            @click="runLoadTest"
          >
            <template #icon><i class="fa fa-bolt"></i></template>
            {{ loadTestRunning ? '테스트 중...' : '테스트 시작' }}
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- 중복응모 테스트 모달 (원자성 검증) -->
    <a-modal
      v-model:visible="duplicateTestModalVisible"
      title="중복응모 테스트 (원자성 검증)"
      :footer="null"
      width="500px"
    >
      <div class="duplicate-test-modal">
        <a-alert
          message="findOneAndUpdate 원자성 테스트"
          description="같은 전화번호로 동시에 여러 요청을 보내 쿠폰이 1개만 발급되는지 확인합니다."
          type="info"
          show-icon
          style="margin-bottom: 16px;"
        />

        <!-- 테스트 대상 정보 -->
        <div v-if="duplicateTestTarget" class="target-info">
          <a-descriptions :column="1" size="small" bordered>
            <a-descriptions-item label="이름">
              {{ duplicateTestTarget.customerName || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="전화번호">
              {{ duplicateTestTarget.customerMobile }}
            </a-descriptions-item>
          </a-descriptions>
        </div>

        <a-form layout="vertical" style="margin-top: 16px;">
          <a-form-item label="동시 요청 수">
            <a-input-number
              v-model:value="duplicateTestConfig.concurrentRequests"
              :min="2"
              :max="50"
              style="width: 100%;"
            />
            <div class="form-hint">2~50 사이 값 (권장: 10~20)</div>
          </a-form-item>
        </a-form>

        <!-- 실행 결과 표시 -->
        <div v-if="duplicateTestRunning || duplicateTestResults.total > 0" class="duplicate-test-results">
          <a-divider>테스트 결과</a-divider>
          <a-row :gutter="[8, 8]">
            <a-col :span="6">
              <a-statistic title="신규등록" :value="duplicateTestResults.success" :value-style="{ color: duplicateTestResults.success <= 1 ? '#3f8600' : '#cf1322' }">
                <template #prefix><i class="fa fa-plus-circle"></i></template>
              </a-statistic>
            </a-col>
            <a-col :span="6">
              <a-statistic title="이미등록" :value="duplicateTestResults.alreadyRegistered" :value-style="{ color: '#1890ff' }">
                <template #prefix><i class="fa fa-check-circle"></i></template>
              </a-statistic>
            </a-col>
            <a-col :span="6">
              <a-statistic title="429" :value="duplicateTestResults.rateLimited" :value-style="{ color: '#faad14' }">
                <template #prefix><i class="fa fa-ban"></i></template>
              </a-statistic>
            </a-col>
            <a-col :span="6">
              <a-statistic title="실패" :value="duplicateTestResults.failed" :value-style="{ color: '#999' }">
                <template #prefix><i class="fa fa-times-circle"></i></template>
              </a-statistic>
            </a-col>
          </a-row>

          <!-- 원자성 검증 결과 -->
          <div v-if="!duplicateTestRunning && duplicateTestResults.total > 0" style="margin-top: 16px;">
            <a-alert
              v-if="duplicateTestResults.success <= 1"
              message="원자성 검증 성공"
              :description="`${duplicateTestResults.total}회 동시 요청에서 신규등록 ${duplicateTestResults.success}회 - findOneAndUpdate가 정상 동작합니다.`"
              type="success"
              show-icon
            />
            <a-alert
              v-else
              message="원자성 검증 실패"
              :description="`${duplicateTestResults.total}회 동시 요청에서 신규등록 ${duplicateTestResults.success}회 - 중복 발급이 발생했습니다!`"
              type="error"
              show-icon
            />
          </div>

          <a-progress
            v-if="duplicateTestRunning"
            :percent="Math.round((duplicateTestResults.success + duplicateTestResults.alreadyRegistered + duplicateTestResults.rateLimited + duplicateTestResults.failed) / duplicateTestResults.total * 100)"
            status="active"
            style="margin-top: 16px;"
          />
        </div>

        <div class="modal-footer" style="margin-top: 24px; text-align: right;">
          <a-button @click="duplicateTestModalVisible = false" style="margin-right: 8px;">
            닫기
          </a-button>
          <a-button
            type="primary"
            danger
            :loading="duplicateTestRunning"
            @click="runDuplicateTest"
          >
            <template #icon><i class="fa fa-clone"></i></template>
            {{ duplicateTestRunning ? '테스트 중...' : '테스트 시작' }}
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, watch, watchEffect, onMounted, onUnmounted, inject, getCurrentInstance, h } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { message, Tooltip, Typography, Modal } from 'ant-design-vue';
import { twoBtnDialog } from '@/utils/utils';
import eventsApis from '../../api.js';
import dayjs from 'dayjs';
import ParticipantDetailDrawer from './ParticipantDetailDrawer.vue';
import MessageSendDrawer from './MessageSendDrawer.vue';
import AttendeeDeleteDrawer from './AttendeeDeleteDrawer.vue';
import EventResourceDrawer from '../EventResourceDrawer.vue';
import ThermageMigrationDrawer from './ThermageMigrationDrawer.vue';
import OrganizerCertificateMigrationDrawer from './OrganizerCertificateMigrationDrawer.vue';

const { Text: TypographyText } = Typography;

export default {
  name: 'ParticipantMonitoring',
  components: {
    ParticipantDetailDrawer,
    MessageSendDrawer,
    AttendeeDeleteDrawer,
    EventResourceDrawer,
    ThermageMigrationDrawer,
    OrganizerCertificateMigrationDrawer,
    ATypographyText: TypographyText
  },
  props: {
    participants: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    eventList: {
      type: Array,
      default: () => []
    },
    selectedEventId: {
      type: String,
      default: 'all'
    }
  },
  emits: ['filter-change', 'event-change', 'send-message'],
  setup(props, { emit }) {
    // Router 인스턴스
    const router = useRouter();
    const route = useRoute();

    // 필터 영역 접기/펼치기 상태
    const isFilterCollapsed = ref(true); // Filter section collapsed by default
    const isEventInfoCollapsed = ref(true); // Event info section collapsed by default
    const isPerformanceAnalysisCollapsed = ref(true); // Performance analysis section collapsed by default

    // 자료실 Drawer 상태
    const showResourceDrawer = ref(false);

    // 써마지 마이그레이션 Drawer 상태
    const thermageMigrationDrawerVisible = ref(false);

    // 주최자 인증서 마이그레이션 Drawer 상태
    const organizerCertMigrationDrawerVisible = ref(false);

    // 고객 이벤트 참여 이력 조회 모달 상태
    // 반응형: 모바일 여부 감지
    const isMobile = ref(false);
    const isTablet = ref(false);
    const MOBILE_BREAKPOINT = 767;
    const TABLET_BREAKPOINT = 1199;

    const checkScreenSize = () => {
      isMobile.value = window.innerWidth <= MOBILE_BREAKPOINT;
      isTablet.value = window.innerWidth > MOBILE_BREAKPOINT && window.innerWidth <= TABLET_BREAKPOINT;
    };

    // API 인스턴스 초기화
    const instance = getCurrentInstance();
    let apiMethods = null;
    const apiInstance = ref(null);

    if (instance && instance.proxy) {
      apiMethods = eventsApis(instance.proxy.$apiInstance, instance.proxy.$store);
      apiInstance.value = apiMethods;
      console.log('ParticipantMonitoring - apiInstance initialized:', apiInstance.value);
      console.log('ParticipantMonitoring - bulkDeleteAttendees:', apiInstance.value?.bulkDeleteAttendees);
    } else {
      console.error('Current instance not available, API initialization failed');
      message.error('시스템 초기화 오류가 발생했습니다.');
      return {};
    }

    // API functions 가져오기
    const getEventsWithStatus = apiMethods.getEventsWithStatus;
    const getEventAttendeesWithActivities = apiMethods.getEventAttendeesWithActivities;

    const searchKeyword = ref('');
    const hospitalSearchKeyword = ref(''); // 병원 이름 검색
    const selectedRowKeys = ref([]);
    const selectedRows = ref([]);
    // 성능 최적화: O(1) 조회를 위한 Set (includes 대신 has 사용)
    const selectedRowKeysSet = computed(() => new Set(selectedRowKeys.value));
    const selectedCouponInfo = ref(null);
    const selectedCouponNumber = ref(null);
    const selectedDateType = ref('participatedAt'); // 기본값: 이벤트 참여시간
    const minCouponCount = ref(null); // 최소 쿠폰 수 필터
    const selectedDateRange = ref(null);
    const selectedReviewFilter = ref(null); // 리뷰 필터 상태

    const selectedReviewContentFilter = ref(null); // 리뷰 컨텐츠 필터 상태
    const selectedMarketingConsent = ref(null); // 마케팅 수신 동의 필터
    const selectedEventMetadataFilter = ref(null); // 과거 이벤트 참여 이력 필터
    const selectedPrimarySegment = ref(null); // Primary 세그먼트 필터
    const selectedSecondarySegment = ref([]); // Secondary 세그먼트 필터 (복수 선택)
    const selectedShareFilter = ref(null); // 공유 여부 필터
    const minShareCount = ref(null); // 공유 횟수 필터
    const selectedDeleteYnFilter = ref('exclude'); // 삭제 여부 필터 (기본값: 삭제 고객 제외)
    const shareCountOperator = ref('gte'); // 공유 횟수 연산자 (gte: 이상, lte: 이하)

    // 세그먼트 동기화 관련 상태
    const segmentSyncStatus = ref('idle'); // idle, processing, completed, failed
    const segmentSyncProgress = ref({
      remainingCount: 0,
      syncedCount: 0,
      totalCount: 0,
      percentage: 0
    });
    const segmentPollingInterval = ref(null);
    const segmentPollingCount = ref(0);
    const segmentPollingStartTime = ref(null);
    const segmentElapsedTime = ref('');

    // 펼쳐보기 상태 관리
    const expandedEvents = ref({});
    const expandedHospitals = ref({});

    // Drawer 상태
    const detailDrawerVisible = ref(false);
    const selectedParticipant = ref({});
    const messageDrawerVisible = ref(false);
    const messageRecipients = ref([]);
    const deleteDrawerVisible = ref(false);
    
    // 새로운 필터 상태
    // URL 쿼리 파라미터에서 eventId 우선 사용, 없으면 props.selectedEventId 사용
    const initialEventId = route.query.eventId || props.selectedEventId;
    const selectedEvent = ref(initialEventId);
    const selectedActivityTypes = ref([]);
    const searchAllEvents = ref(false); // 전체 이벤트 검색 체크박스 상태
    
    // 페이지네이션 상태
    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `총 ${total.toLocaleString()}명`,
      pageSizeOptions: ['10', '20', '50', '100', '150', '200', '300', '500']
    });
    
    // API 로딩 상태
    const eventsLoading = ref(false);
    
    // API 데이터 상태
    const eventList = ref([]);
    const availableActivityTypes = ref([]);
    const participantData = ref([]);
    const budgetStats = ref({ totalSpentAmount: 0, issuedCouponCount: 0 }); // 백엔드에서 계산된 예산 통계
    const currentEventData = ref(null); // 현재 선택된 이벤트의 전체 데이터 저장

    // AARRR 그룹 접기/펼치기 상태 (미분류만 기본 접기)
    const collapsedGroups = ref({
      acquisition: false,
      activation: false,
      retention: false,
      revenue: false,
      referral: false,
      uncategorized: true // 미분류만 기본 접기
    });

    const toggleGroup = (groupName) => {
      collapsedGroups.value[groupName] = !collapsedGroups.value[groupName];
    };

    // props 변경 감지하여 동기화 및 자동 조회
    watchEffect(() => {
      if (props.selectedEventId && props.selectedEventId !== selectedEvent.value) {
        selectedEvent.value = props.selectedEventId;
        // 이벤트가 변경되면 자동으로 참여자 로드
        if (props.selectedEventId !== 'all' && props.selectedEventId !== '') {
          console.log('Event changed via props, loading participants:', props.selectedEventId);

          // eventList에서 해당 이벤트 찾아서 currentEventData 설정
          const selectedEventObj = eventList.value.find(e => e._id === props.selectedEventId);
          if (selectedEventObj) {
            currentEventData.value = selectedEventObj;
            console.log('✅ currentEventData 설정 완료 (props):', selectedEventObj.eventTitle);
          } else {
            console.log('⚠️ eventList에서 이벤트를 찾지 못함, eventList 개수:', eventList.value.length);
          }

          pagination.value.current = 1; // 페이지 리셋
          loadParticipants();
        }
      }
    });

    // eventList가 로드되면 selectedEvent에 해당하는 currentEventData 설정
    watch([eventList, selectedEvent], ([newEventList, newSelectedEvent]) => {
      if (newEventList.length > 0 && newSelectedEvent) {
        const selectedEventObj = newEventList.find(e => e._id === newSelectedEvent);
        if (selectedEventObj && selectedEventObj !== currentEventData.value) {
          currentEventData.value = selectedEventObj;
          console.log('✅ currentEventData 설정 완료 (watch):', selectedEventObj.eventTitle);
        }
      }
    });

    // 이벤트 타이틀 컬럼 (전체 이벤트 검색 시에만 표시)
    const eventTitleColumn = {
      title: '이벤트',
      dataIndex: ['eventInfo', 'eventTitle'],
      key: 'eventTitle',
      width: 150,
      fixed: 'left',
      ellipsis: true,
      customRender: ({ record }) => {
        const eventTitle = record.eventInfo?.eventTitle || record.eventTitle || '-';
        return h(Tooltip, { title: eventTitle }, () =>
          h('span', { style: 'cursor: pointer;' }, eventTitle)
        );
      }
    };

    // 컬럼 정의
    const columns = computed(() => {
      const baseColumns = [
        {
          title: '번호',
          key: 'index',
          width: 60,
          fixed: 'left',
          align: 'center',
          customRender: ({ index }) => {
            return (pagination.value.current - 1) * pagination.value.pageSize + index + 1;
          }
        },
        {
          title: '삭제표기',
          key: 'deleteYn',
          width: 80,
          fixed: 'left',
          align: 'center'
        },
        // 전체 이벤트 검색 시에만 이벤트 타이틀 컬럼 추가
        ...(searchAllEvents.value ? [eventTitleColumn] : []),
        {
        title: () => h('span', [
          '고객분류',
          h(Tooltip, {
            title: h('div', { style: 'max-width: 400px;' }, [
              h('div', { style: 'font-weight: bold; margin-bottom: 8px;' }, '📊 세그먼트 기반 고객 분류'),
              h('div', { style: 'margin-bottom: 6px;' }, [
                h('b', 'Primary: '),
                '주요 분류 (CERTIFICATE_HOLDER, NON_CERTIFICATE 등)'
              ]),
              h('div', { style: 'margin-bottom: 6px;' }, [
                h('b', 'Secondary: '),
                '세부 분류 (EVENT_PARTICIPANT, NEW_CERTIFICATE 등)'
              ]),
              h('div', { style: 'margin-bottom: 6px;' }, [
                h('b', 'Certificates: '),
                '보유 인증서 개수'
              ])
            ])
          }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        key: 'segment',
        width: 200,
        fixed: 'left'
      },
      {
        title: () => h('span', [
          '이름',
          h(Tooltip, { title: '참여자: eventattendees 컬렉션 / 고객: customers 컬렉션' }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        key: 'name',
        width: 140,
        fixed: 'left'
      },
      {
        title: () => h('span', [
          '연락처',
          h(Tooltip, { title: '참여자: eventattendees 컬렉션 / 고객: customers 컬렉션' }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        key: 'mobile',
        width: 170
      },
      {
        title: '병원',
        dataIndex: 'hospital',
        key: 'hospital',
        width: 180
      },
      {
        title: '참여 단계',
        key: 'progress',
        width: 200,
        align: 'center'
      },
      {
        title: () => h('span', [
          '마수동',
          h(Tooltip, { title: '마케팅수신동의' }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: { marginLeft: '4px', color: '#999', fontSize: '12px' } })
          )
        ]),
        key: 'marketingConsent',
        width: 80,
        align: 'center'
      },
      {
        title: '현재 상태',
        key: 'status',
        width: 100,
        align: 'center'
      },
      {
        title: '이벤트 참여일',
        dataIndex: 'participatedAt',
        key: 'participatedAt',
        width: 150
      },
      {
        title: () => h('span', [
          '과거 이벤트 참여이력',
          h(Tooltip, { title: '이전에 참여한 이벤트 목록' }, () =>
            h('i', { class: 'fa fa-question-circle help-icon', style: 'margin-left: 4px' })
          )
        ]),
        key: 'eventMetadata',
        width: 300
      },
      {
        title: '복사/링크',
        key: 'copyActions',
        width: 110,
        align: 'center'
      },
      {
        title: '액션',
        key: 'actions',
        width: 100,
        fixed: 'right',
        align: 'center'
      }
    ];

    return baseColumns;
    });

    // 참여자 통계
    const stats = computed(() => {
      return {
        total: props.participants.length
      };
    });

    // 소진금액 계산 (백엔드에서 전체 이벤트 기준으로 집계된 값 사용)
    const spentBudget = computed(() => {
      return {
        amount: budgetStats.value.totalSpentAmount || 0,
        couponAmount: budgetStats.value.couponSpentAmount || 0,
        couponCount: budgetStats.value.issuedCouponCount || 0,
        expenseAmount: budgetStats.value.expenseSpentAmount || 0,
        expenseCount: budgetStats.value.expenseCount || 0
      };
    });

    // 소진금액 카드 펼침 상태
    const isSpentBudgetExpanded = ref(false);

    // 총예산 카드 펼침 상태
    const isTotalBudgetExpanded = ref(false);

    // 세그먼트 동기화 버튼 텍스트
    const segmentSyncButtonText = computed(() => {
      switch(segmentSyncStatus.value) {
        case 'processing':
          return `동기화 중... (${segmentSyncProgress.value.percentage || 0}%)`;
        case 'completed':
          return '재동기화';
        case 'failed':
          return '재시도';
        default:
          return '고객 세그멘테이션';
      }
    });

    const activeFilterCount = computed(() => {
      let count = 0;
      if (selectedEvent.value && selectedEvent.value !== '') count++;
      if (selectedActivityTypes.value.length > 0) count++;
      if (searchKeyword.value) count++;
      if (hospitalSearchKeyword.value) count++;
      if (selectedEventMetadataFilter.value) count++;
      if (selectedPrimarySegment.value) count++;
      if (selectedSecondarySegment.value && selectedSecondarySegment.value.length > 0) count++;
      if (selectedCouponInfo.value) count++;
      if (selectedCouponNumber.value) count++;
      if (minCouponCount.value && minCouponCount.value > 0) count++;
      if (selectedReviewFilter.value) count++;
      if (selectedReviewContentFilter.value) count++;
      if (selectedMarketingConsent.value) count++;
      if (selectedDateRange.value && selectedDateRange.value.length === 2) count++;
      if (selectedShareFilter.value) count++;
      if (minShareCount.value && minShareCount.value > 0) count++;
      if (selectedDeleteYnFilter.value) count++;
      return count;
    });

    // 적용된 필터 태그 목록
    const activeFilterTags = computed(() => {
      const tags = [];

      if (searchAllEvents.value) {
        tags.push({ key: 'searchAllEvents', label: '전체 이벤트', color: 'purple' });
      }
      if (selectedEvent.value && selectedEvent.value !== '' && !searchAllEvents.value) {
        const event = eventList.value.find(e => e._id === selectedEvent.value);
        const eventName = event?.eventTitle || selectedEvent.value;
        const truncatedLabel = eventName.length > 10 ? eventName.slice(0, 10) + '...' : eventName;
        tags.push({
          key: 'event',
          label: `이벤트: ${truncatedLabel}`,
          fullLabel: `이벤트: ${eventName}`,
          eventId: selectedEvent.value,
          color: 'blue'
        });
      }
      if (selectedActivityTypes.value.length > 0) {
        tags.push({ key: 'activity', label: `참여단계: ${selectedActivityTypes.value.length}개`, color: 'cyan' });
      }
      if (searchKeyword.value) {
        const keyword = searchKeyword.value;
        const truncatedKeyword = keyword.length > 8 ? keyword.slice(0, 8) + '...' : keyword;
        tags.push({
          key: 'keyword',
          label: `검색: ${truncatedKeyword}`,
          fullLabel: keyword.length > 8 ? `검색: ${keyword}` : undefined,
          color: 'orange'
        });
      }
      if (hospitalSearchKeyword.value) {
        const keyword = hospitalSearchKeyword.value;
        const truncatedKeyword = keyword.length > 8 ? keyword.slice(0, 8) + '...' : keyword;
        tags.push({
          key: 'hospital',
          label: `병원: ${truncatedKeyword}`,
          fullLabel: keyword.length > 8 ? `병원: ${keyword}` : undefined,
          color: 'purple'
        });
      }
      if (selectedEventMetadataFilter.value) {
        const label = selectedEventMetadataFilter.value === 'exists' ? '있음' : '없음';
        tags.push({ key: 'eventMetadata', label: `과거이력: ${label}`, color: 'geekblue' });
      }
      if (selectedPrimarySegment.value) {
        tags.push({ key: 'primary', label: `1차분류: ${selectedPrimarySegment.value}`, color: 'green' });
      }
      if (selectedSecondarySegment.value && selectedSecondarySegment.value.length > 0) {
        tags.push({ key: 'secondary', label: `2차분류: ${selectedSecondarySegment.value.length}개`, color: 'lime' });
      }
      if (selectedCouponInfo.value) {
        const label = selectedCouponInfo.value === 'exists' ? '있음' : '없음';
        tags.push({ key: 'couponInfo', label: `쿠폰권한: ${label}`, color: 'gold' });
      }
      if (selectedCouponNumber.value) {
        const label = selectedCouponNumber.value === 'exists' ? '수령' : '미수령';
        tags.push({ key: 'couponNumber', label: `쿠폰: ${label}`, color: 'volcano' });
      }
      if (minCouponCount.value && minCouponCount.value > 0) {
        tags.push({ key: 'minCoupon', label: `쿠폰≥${minCouponCount.value}`, color: 'magenta' });
      }
      if (selectedReviewFilter.value) {
        const label = selectedReviewFilter.value === 'exists' ? '있음' : '없음';
        tags.push({ key: 'review', label: `리뷰: ${label}`, color: 'purple' });
      }
      if (selectedReviewContentFilter.value) {
        const label = selectedReviewContentFilter.value === 'exists' ? '있음' : '없음';
        tags.push({ key: 'reviewContent', label: `리뷰내용: ${label}`, color: 'pink' });
      }
      if (selectedMarketingConsent.value) {
        const label = selectedMarketingConsent.value === 'agreed' ? '동의' : '철회';
        tags.push({ key: 'marketing', label: `마수동: ${label}`, color: 'red' });
      }
      if (selectedDateRange.value && selectedDateRange.value.length === 2) {
        tags.push({ key: 'dateRange', label: '기간 설정됨', color: 'default' });
      }
      if (selectedShareFilter.value) {
        const label = selectedShareFilter.value === 'exists' ? '공유함' : '미공유';
        tags.push({ key: 'share', label: `공유: ${label}`, color: 'cyan' });
      }
      if (minShareCount.value && minShareCount.value > 0) {
        const op = shareCountOperator.value === 'lte' ? '≤' : '≥';
        tags.push({ key: 'minShare', label: `공유${op}${minShareCount.value}`, color: 'geekblue' });
      }
      if (selectedDeleteYnFilter.value) {
        const label = selectedDeleteYnFilter.value === 'exclude' ? '제외' : '삭제만';
        tags.push({ key: 'deleteYn', label: `삭제고객: ${label}`, color: 'red' });
      }

      return tags;
    });

    // 보상조건 태그 목록 (접힌 상태에서 표시)
    const rewardThresholdTags = computed(() => {
      const thresholds = currentEventData.value?.rewardThresholds || [];
      if (thresholds.length === 0) return [];

      return thresholds.map((reward, index) => {
        const conditionLabel = getConditionTypeLabel(reward.conditionType);
        const rewardName = reward.rewardName || '';
        const truncatedName = rewardName.length > 15 ? rewardName.slice(0, 15) + '...' : rewardName;
        const isActive = reward.isActive !== false;

        return {
          key: `reward-${index}`,
          label: `${conditionLabel}: ${truncatedName}`,
          fullLabel: `${conditionLabel} ${reward.thresholdValue}회: ${rewardName}`,
          color: isActive ? getConditionTypeColor(reward.conditionType) : 'default',
          isActive
        };
      });
    });

    // 필터링된 데이터 (API에서 가져온 데이터 사용)
    // AARRR 메트릭 기반 그룹핑 computed
    const groupedActivityTypes = computed(() => {
      const groups = {
        acquisition: [],
        activation: [],
        retention: [],
        revenue: [],
        referral: [],
        uncategorized: []
      };

      console.log('🔍 groupedActivityTypes computed 실행됨');
      console.log('  - currentEventData:', currentEventData.value?._id, currentEventData.value?.eventTitle);
      console.log('  - aarrrMetrics 존재:', !!currentEventData.value?.aarrrMetrics);
      console.log('  - availableActivityTypes 개수:', availableActivityTypes.value.length);

      if (!currentEventData.value?.aarrrMetrics || availableActivityTypes.value.length === 0) {
        // aarrrMetrics가 없으면 모두 미분류로 표시
        groups.uncategorized = [...availableActivityTypes.value];
        console.log('⚠️ aarrrMetrics가 없거나 availableActivityTypes가 비어있음');
        console.log('  - currentEventData:', currentEventData.value);
        console.log('  - availableActivityTypes:', availableActivityTypes.value);
        return groups;
      }

      const metrics = currentEventData.value.aarrrMetrics;

      console.log('📊 현재 이벤트 aarrrMetrics:', JSON.stringify(metrics, null, 2));
      console.log('📋 Available Activity Types:', availableActivityTypes.value.map(a => ({ type: a.type, label: a.label })));

      // 각 AARRR 그룹의 activityType을 Map으로 저장 (우선순위: acquisition > activation > retention > revenue > referral)
      const typeToGroup = new Map();

      // 우선순위 역순으로 등록 (나중에 등록된 것이 우선순위가 높음)
      // activityType과 displayName 모두 사용하여 매칭
      ['referral', 'revenue', 'retention', 'activation', 'acquisition'].forEach(groupName => {
        const groupMetrics = metrics[groupName] || [];
        groupMetrics.forEach(metric => {
          // activityType 우선, 없으면 displayName 사용
          const activityType = metric.activityType;
          const displayName = metric.displayName;

          if (activityType) {
            typeToGroup.set(activityType, groupName);
          }
          if (displayName && displayName !== activityType) {
            // displayName이 별도로 존재하면 추가 매핑
            typeToGroup.set(displayName, groupName);
          }
        });
      });

      console.log('🗺️ Activity Type to Group Map:', Array.from(typeToGroup.entries()));

      // availableActivityTypes를 AARRR 그룹에 분류
      availableActivityTypes.value.forEach(activityType => {
        const type = activityType.type;
        const groupName = typeToGroup.get(type);

        if (groupName) {
          groups[groupName].push(activityType);
          console.log(`✅ "${type}" → ${groupName}`);
        } else {
          groups.uncategorized.push(activityType);
          console.log(`❓ "${type}" → uncategorized (매칭 실패)`);
        }
      });

      console.log('📈 AARRR 그룹핑 최종 결과:', {
        acquisition: groups.acquisition.length,
        activation: groups.activation.length,
        retention: groups.retention.length,
        revenue: groups.revenue.length,
        referral: groups.referral.length,
        uncategorized: groups.uncategorized.length
      });

      return groups;
    });

    // 각 AARRR 그룹의 누적 합계 계산
    const groupTotalCounts = computed(() => {
      const totals = {
        acquisition: 0,
        activation: 0,
        retention: 0,
        revenue: 0,
        referral: 0,
        uncategorized: 0
      };

      Object.keys(totals).forEach(groupName => {
        totals[groupName] = groupedActivityTypes.value[groupName].reduce(
          (sum, activityType) => sum + (activityType.count || 0),
          0
        );
      });

      return totals;
    });

    const filteredData = computed(() => {
      // participantData가 배열인지 확인
      if (!Array.isArray(participantData.value)) {
        console.warn('participantData is not an array:', participantData.value);
        return [];
      }
      return participantData.value.map(item => ({ ...item, key: item.id || item._id }));
    });

    // 메서드
    const handleFilterChange = (value) => {
      emit('filter-change', value);
      selectedRowKeys.value = [];
      selectedRows.value = [];
    };

    // 전화번호 포맷팅 함수
    const formatPhoneNumber = (value) => {
      // 하이픈 제거
      const numbers = value.replace(/[^0-9]/g, '');

      // 전화번호 포맷에 맞지 않으면 원본 반환
      if (numbers.length < 4) return numbers;

      // 010-1234-5678 형식으로 포맷팅
      if (numbers.length <= 7) {
        return numbers.replace(/(\d{3})(\d{1,4})/, '$1-$2');
      } else if (numbers.length <= 11) {
        return numbers.replace(/(\d{3})(\d{4})(\d{1,4})/, '$1-$2-$3');
      }

      // 11자리 초과 시 11자리까지만 포맷팅
      return numbers.slice(0, 11).replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    };

    const formatPhoneNumberInput = (e) => {
      const value = e.target.value;
      // 숫자만 있는 경우에만 포맷팅 적용 (이름 검색과 구분)
      if (/^\d/.test(value.replace(/[^0-9]/g, ''))) {
        searchKeyword.value = formatPhoneNumber(value);
      }
    };

    const handleSearch = () => {
      console.log('Searching:', searchKeyword.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    // 병원 검색 핸들러
    const handleHospitalSearch = () => {
      console.log('Hospital searching:', hospitalSearchKeyword.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    // 병원 검색 입력 변경 핸들러 (clear 버튼 클릭 시에도 동작)
    const handleHospitalSearchChange = (e) => {
      // clear 버튼 클릭 시 value가 빈 문자열이 됨
      if (e.target.value === '' && hospitalSearchKeyword.value === '') {
        console.log('Hospital search cleared');
        pagination.value.current = 1;
        loadParticipants();
      }
    };

    // 전체 이벤트 검색 체크박스 변경 핸들러 (설정만 변경, 실제 조회는 새로고침 시)
    const handleSearchAllEventsChange = () => {
      console.log('전체 이벤트 검색 설정 변경:', searchAllEvents.value);
      // 체크박스는 설정만 변경하고, 실제 조회는 새로고침 버튼 클릭 시 수행
    };

    const handleRefresh = () => {
      loadEvents();
      loadParticipants();
      message.success('데이터를 새로고침했습니다.');
    };
    
    const handleReviewFilterChange = () => {
      console.log('Review filter changed:', selectedReviewFilter.value);
      
      // 리뷰 유무가 "있음"이 아닌 경우 리뷰 내용 유무 필터 초기화
      if (selectedReviewFilter.value !== 'exists') {
        selectedReviewContentFilter.value = null;
      }
      
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };
    
    const handleReviewContentFilterChange = () => {
      console.log('Review content filter changed:', selectedReviewContentFilter.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };
    
    const handleMarketingConsentChange = () => {
      console.log('Marketing consent filter changed:', selectedMarketingConsent.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    const handleShareFilterChange = () => {
      console.log('Share filter changed:', selectedShareFilter.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    const handleDeleteYnFilterChange = () => {
      console.log('DeleteYn filter changed:', selectedDeleteYnFilter.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    const handleMinShareCountChange = () => {
      console.log('Min share count changed:', minShareCount.value);
      pagination.value.current = 1; // 페이지를 1로 리셋
      loadParticipants();
    };

    const handleShareCountOperatorChange = () => {
      console.log('Share count operator changed:', shareCountOperator.value);
      if (minShareCount.value && minShareCount.value > 0) {
        pagination.value.current = 1; // 페이지를 1로 리셋
        loadParticipants();
      }
    };

    const onSelectChange = (keys, rows) => {
      selectedRowKeys.value = keys;
      selectedRows.value = rows;
    };

    // 모바일 카드: 전체 선택
    const handleSelectAll = (e) => {
      if (e.target.checked) {
        selectedRowKeys.value = filteredData.value.map(item => item._id);
        selectedRows.value = [...filteredData.value];
      } else {
        selectedRowKeys.value = [];
        selectedRows.value = [];
      }
    };

    // 모바일 카드: 개별 선택 (Set 기반 O(1) 조회로 최적화)
    const handleCardSelect = (record, checked) => {
      if (checked) {
        if (!selectedRowKeysSet.value.has(record._id)) {
          selectedRowKeys.value = [...selectedRowKeys.value, record._id];
          selectedRows.value = [...selectedRows.value, record];
        }
      } else {
        selectedRowKeys.value = selectedRowKeys.value.filter(key => key !== record._id);
        selectedRows.value = selectedRows.value.filter(row => row._id !== record._id);
      }
    };

    // 모바일 카드: 페이지 변경
    const handleMobilePageChange = (page, pageSize) => {
      pagination.value.current = page;
      if (pageSize) {
        pagination.value.pageSize = pageSize;
      }
      loadParticipants();
    };

    const handleSendMessage = () => {
      messageRecipients.value = selectedRows.value;
      messageDrawerVisible.value = true;
    };

    const sendIndividualMessage = (record) => {
      messageRecipients.value = [record];
      messageDrawerVisible.value = true;
    };
    
    const handleMessageSent = (data) => {
      console.log('Message sent:', data);
      emit('send-message', data);
    };

    // 참여자 삭제 버튼 클릭
    const handleDeleteAttendees = () => {
      if (selectedRowKeys.value.length === 0) {
        message.warning('삭제할 참여자를 선택해주세요.');
        return;
      }
      deleteDrawerVisible.value = true;
    };

    // 삭제 성공 후 처리
    const handleDeleteSuccess = (deleteType, data) => {
      console.log('Delete success:', deleteType, data);

      // 선택 초기화
      selectedRowKeys.value = [];
      selectedRows.value = [];

      // 데이터 새로고침
      loadParticipants();
    };

    // 써마지 마이그레이션 완료 후 처리
    const handleThermageMigrationComplete = () => {
      console.log('Thermage migration complete');
      // 데이터 새로고침
      loadParticipants();
    };

    const viewDetail = (record) => {
      console.log('View detail:', record);
      selectedParticipant.value = record;
      detailDrawerVisible.value = true;
    };

    // 참여정보 초기화 함수
    const resetParticipationInfo = async (record) => {
      const customerName = record.customerName || record.customerInfo?.name || '참여자';
      const customerMobile = record.customerMobile || record.customerInfo?.mobile || '연락처 없음';

      const confirmText = `정말로 ${customerName}님(${customerMobile})의 참여정보를 초기화하시겠습니까? 이 작업은 되돌릴 수 없습니다.`;

      const isConfirmed = await twoBtnDialog(
        '참여정보 초기화',
        confirmText,
        '초기화'
      );

      if (isConfirmed) {
        // API 호출
        const customerId = record.customerId;
        const eventId = selectedEvent.value;

        if (!customerId) {
          message.error('고객 ID가 없어 초기화할 수 없습니다.');
          return;
        }

        if (!eventId) {
          message.error('이벤트를 선택해주세요.');
          return;
        }

        message.loading('참여정보를 초기화하는 중...', 0);

        if (apiMethods && apiMethods.resetParticipationInfo) {
          apiMethods.resetParticipationInfo(
            eventId,
            customerId,
            (response) => {
              message.destroy();
              message.success(`${customerName}님의 참여정보가 초기화되었습니다.`);

              // 목록 새로고침
              loadParticipants();

              // 선택된 항목에서 제거
              const index = selectedRows.value.findIndex(r => r._id === record._id);
              if (index > -1) {
                selectedRows.value.splice(index, 1);
                selectedRowKeys.value = selectedRowKeys.value.filter(key => key !== record._id);
              }
            },
            (error) => {
              message.destroy();
              const errorMessage = error.response?.data?.message || '참여정보 초기화에 실패했습니다.';
              message.error(errorMessage);
              console.error('참여정보 초기화 실패:', error);
            }
          );
        } else {
          message.destroy();
          message.error('API 초기화 오류가 발생했습니다.');
        }
      } else {
        console.log('참여정보 초기화 취소');
      }
    };
    
    // 펼쳐보기/접기 토글 함수
    const toggleEventExpand = (recordId) => {
      expandedEvents.value[recordId] = !expandedEvents.value[recordId];
    };

    // 표시할 이벤트 목록 반환 함수
    const getDisplayedEvents = (record) => {
      if (expandedEvents.value[record._id]) {
        return record.eventMetadata;
      }
      return record.eventMetadata.slice(0, 2);
    };

    // 병원 목록 펼쳐보기/접기 토글 함수
    const toggleHospitalExpand = (recordId) => {
      expandedHospitals.value[recordId] = !expandedHospitals.value[recordId];
    };

    // 표시할 병원 목록 반환 함수
    const getDisplayedHospitals = (record) => {
      if (!record.hospitalMetadatas || record.hospitalMetadatas.length === 0) {
        return [];
      }
      if (expandedHospitals.value[record._id]) {
        return record.hospitalMetadatas;
      }
      // 기본적으로 최근 2개만 표시 (배열 끝에서부터)
      return record.hospitalMetadatas.slice(-2).reverse();
    };

    // 개별 이벤트 클릭 핸들러 - 특정 과거 이벤트로 필터링
    const handleSingleEventClick = (record, event) => {
      const eventId = event.eventId;
      
      // 이벤트 리스트에서 해당 이벤트가 있는지 확인
      const eventExists = eventList.value.some(e => e._id === eventId);
      
      if (!eventExists) {
        // 이벤트가 리스트에 없으면 모든 이벤트를 다시 로드
        message.info('과거 이벤트 데이터를 불러오는 중...');
        
        // 모든 상태의 이벤트를 다시 로드 (과거 이벤트 포함)
        getEventsWithStatus(
          { limit: 200 }, // status 필터 없이 모든 이벤트 조회
          (response) => {
            if (response.data) {
              const events = response.data.items || response.data.data || response.data || [];
              eventList.value = Array.isArray(events) ? events : [];
              
              // 과거 이벤트 선택
              selectedEvent.value = eventId;
              // 검색어 필터에 참여자 연락처 설정 (포맷팅 적용)
              searchKeyword.value = formatPhoneNumber(record.customerMobile || '');
              
              // 필터 적용을 위해 데이터 재조회
              pagination.value.current = 1;
              loadParticipants();
              
              message.success(`'${record.customerName}' 참여자의 이벤트 '${event.eventTitle}'로 필터링되었습니다.`);
            }
          },
          (error) => {
            console.error('이벤트 목록 로드 실패:', error);
            message.error('과거 이벤트를 불러오는 중 오류가 발생했습니다.');
          }
        );
      } else {
        // 이벤트가 이미 리스트에 있으면 바로 선택
        selectedEvent.value = eventId;
        // 검색어 필터에 참여자 연락처 설정 (포맷팅 적용)
        searchKeyword.value = formatPhoneNumber(record.customerMobile || '');
        
        // 필터 적용을 위해 데이터 재조회
        pagination.value.current = 1;
        loadParticipants();
        
        message.success(`'${record.customerName}' 참여자의 이벤트 '${event.eventTitle}'로 필터링되었습니다.`);
      }
    };
    
    // 고객 아이디 복사
    const copyEventId = () => {
      if (selectedEvent.value) {
        navigator.clipboard.writeText(selectedEvent.value).then(() => {
          message.success(`이벤트 ID가 복사되었습니다: ${selectedEvent.value}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('선택된 이벤트가 없습니다.');
      }
    };

    const copyCustomerId = (record) => {
      if (record.customerId) {
        navigator.clipboard.writeText(record.customerId).then(() => {
          message.success(`고객 아이디가 복사되었습니다: ${record.customerId}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('고객 아이디가 없습니다.');
      }
    };
    
    // 이벤트 참여자 아이디 복사
    const copyEventAttendeesId = (record) => {
      const attendeeId = record._id || record.id;
      if (attendeeId) {
        navigator.clipboard.writeText(attendeeId).then(() => {
          message.success(`이벤트 참여자 아이디가 복사되었습니다: ${attendeeId}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('이벤트 참여자 아이디가 없습니다.');
      }
    };

    // deviceId 복사 함수
    const copyDeviceId = (record) => {
      const deviceId = record.deviceId;
      if (deviceId) {
        navigator.clipboard.writeText(deviceId).then(() => {
          message.success(`deviceId가 복사되었습니다: ${deviceId}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('deviceId가 없습니다.');
      }
    };

    // referrerId 복사 함수
    const copyReferrerId = (record) => {
      const referrerId = record.referrerId;
      if (referrerId) {
        navigator.clipboard.writeText(referrerId).then(() => {
          message.success(`referrerId가 복사되었습니다: ${referrerId}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('referrerId가 없습니다.');
      }
    };

    // referrerId2 복사 함수
    const copyReferrerId2 = (record) => {
      const referrerId2 = record.referrerId2;
      if (referrerId2) {
        navigator.clipboard.writeText(referrerId2).then(() => {
          message.success(`referrerId2가 복사되었습니다: ${referrerId2}`);
        }).catch(() => {
          message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
        });
      } else {
        message.warning('referrerId2가 없습니다.');
      }
    };

    // URL 도메인 설정 함수 - 현재 도메인의 프리픽스를 기준으로 설정
    const getCustomerDomain = () => {
      const currentDomain = window.location.hostname;

      // 현재 도메인에서 프리픽스 추출
      if (currentDomain.includes('localhost') || currentDomain.includes('127.0.0.1')) {
        return 'https://local-customer.certi.life';
      } else if (currentDomain.startsWith('operation.certi.life')) {
        // 운영 환경 (프리픽스 없음)
        return 'https://customer.certi.life';
      } else if (currentDomain.startsWith('stg-')) {
        // 스테이징 환경
        return 'https://intest-customer.certi.life';
      } else if (currentDomain.startsWith('dev-')) {
        // 개발 환경
        return 'https://dev-customer.certi.life';
      } else if (currentDomain.startsWith('intest-')) {
        // intest 환경
        return 'https://intest-customer.certi.life';
      } else {
        // 기본값: 운영
        return 'https://customer.certi.life';
      }
    };

    /**
     * shareId 생성 함수
     * web-customer의 shareIdManager.js와 동일한 형식
     * 형식: share_{timestamp}_{random6chars}
     */
    const generateShareId = () => {
      const timestamp = Date.now();
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
      let randomChars = '';
      const array = new Uint8Array(6);
      crypto.getRandomValues(array);
      for (let i = 0; i < 6; i++) {
        randomChars += chars[array[i] % chars.length];
      }
      return `share_${timestamp}_${randomChars}`;
    };

    // 환자 링크 복사 함수 (경로만)
    // 알림톡 URL 형식과 동기화: eventId, customerId, nftNo, from 파라미터 포함
    const copyPatientLink = (record) => {
      const attendeeId = record._id || record.id;
      const eventId = selectedEvent.value;
      const customerId = record.customerId;
      if (!eventId) {
        message.warning('이벤트를 먼저 선택해주세요.');
        return;
      }
      if (!attendeeId) {
        message.warning('이벤트 참여자 아이디가 없습니다.');
        return;
      }

      // nftNo 추출: certificates 배열에서 id 목록 추출 (있는 경우에만)
      let nftNoParam = '';
      if (record.certificates && record.certificates.length > 0) {
        const nftIds = record.certificates.map(cert => cert.id).filter(id => id);
        if (nftIds.length > 0) {
          nftNoParam = `&nftNo=${nftIds.join(',')}`;
        }
      }

      // 알림톡 URL과 동일한 형식 사용
      // from=admin_participation_copy: 관리자 참여현황 페이지에서 복사
      const patientPath = `/event-v2/patient/${eventId}?eventAttendeeId=${attendeeId}&eventId=${eventId}&customerId=${customerId}${nftNoParam}&tenant=zeiss&from=admin_participation_copy&snsType=kakao&locale=ko`;

      navigator.clipboard.writeText(patientPath).then(() => {
        message.success('환자 링크 경로가 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };

    // 친구 링크 복사 함수 (경로만)
    // eventId를 동적으로 사용하고 shareId 추가하여 web-customer와 동기화
    const copyFriendLink = (record) => {
      const attendeeId = record._id || record.id;
      const eventId = selectedEvent.value;
      if (!eventId) {
        message.warning('이벤트를 먼저 선택해주세요.');
        return;
      }
      if (!attendeeId) {
        message.warning('이벤트 참여자 아이디가 없습니다.');
        return;
      }

      // shareId 생성 (SPEC-TRACK-002)
      const shareId = generateShareId();

      // web-customer의 useKakaoShare.js와 동일한 URL 형식 사용
      const friendPath = `/event-v2/friend/${eventId}?referrerId=${attendeeId}&eventId=${eventId}&tenant=zeiss&snsType=kakao&locale=ko&shareId=${shareId}&shareSource=direct`;

      navigator.clipboard.writeText(friendPath).then(() => {
        message.success('친구 링크 경로가 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };

    // 후기 링크 복사 함수
    const copyReviewLink = (record) => {
      if (!selectedEvent.value) {
        message.warning('이벤트를 먼저 선택해주세요.');
        return;
      }
      if (!record.certificates || record.certificates.length === 0) {
        message.warning('인증서 정보가 없습니다.');
        return;
      }

      const nftId = record.certificates[0].id;
      if (!nftId) {
        message.warning('인증서 아이디가 없습니다.');
        return;
      }

      const reviewPath = `/event/share/review?tenant=certilife&locale=ko&snsType=kakao&eventId=${selectedEvent.value}&nftId=${nftId}`;

      navigator.clipboard.writeText(reviewPath).then(() => {
        message.success('후기 링크 경로가 복사되었습니다.');
      }).catch(() => {
        message.error('복사 실패. 브라우저가 클립보드 접근을 지원하지 않습니다.');
      });
    };

    const getStatusColor = (status) => {
      const colors = {
        requested: 'default',
        participated: 'processing',
        shared: 'blue',
        coupon_pending: 'orange',
        coupon_received: 'green',
        logged_in: 'purple'
      };
      return colors[status] || 'default';
    };

    const getStatusText = (status) => {
      const texts = {
        requested: '미참여',
        participated: '참여',
        shared: '공유 완료',
        coupon_pending: '쿠폰 수령 대기',
        coupon_received: '쿠폰 수령 완료',
        logged_in: '로그인 완료'
      };
      return texts[status] || status;
    };

    // Reward condition type label
    const getConditionTypeLabel = (conditionType) => {
      const labels = {
        'share_count': '공유',
        'review_count': '리뷰',
        'entry_count': '응모'
      };
      return labels[conditionType] || conditionType;
    };

    // Reward condition type color
    const getConditionTypeColor = (conditionType) => {
      const colors = {
        'share_count': 'blue',
        'review_count': 'purple',
        'entry_count': 'orange'
      };
      return colors[conditionType] || 'default';
    };

    // Reward target audience label
    const getTargetAudienceLabel = (targetAudience) => {
      const labels = {
        'patient': '환자',
        'friend': '친구',
        'all': '전체'
      };
      return labels[targetAudience] || targetAudience;
    };

    // Reward target audience color
    const getTargetAudienceColor = (targetAudience) => {
      const colors = {
        'patient': 'green',
        'friend': 'cyan',
        'all': 'geekblue'
      };
      return colors[targetAudience] || 'default';
    };

    const getEventStatusText = (status) => {
      const statusTexts = {
        draft: '임시저장',
        preparing: '준비중',
        ongoing: '진행중',
        completed: '완료',
        ended: '종료',
        scheduled: '예정'
      };
      return statusTexts[status] || status;
    };

    // Event status color
    const getEventStatusColor = (status) => {
      const colors = {
        draft: 'default',
        preparing: 'orange',
        ongoing: 'green',
        completed: 'blue',
        ended: 'red',
        scheduled: 'cyan'
      };
      return colors[status] || 'default';
    };

    // Scenario type label
    const getScenarioTypeLabel = (type) => {
      const labels = {
        'REFERRAL': '추천인 이벤트',
        'PARTICIPATION': '참여 이벤트',
        'SURVEY': '설문 이벤트',
        'SIGNUP': '가입 이벤트'
      };
      return labels[type] || type;
    };

    // Scenario type color
    const getScenarioTypeColor = (type) => {
      const colors = {
        'REFERRAL': 'purple',
        'PARTICIPATION': 'blue',
        'SURVEY': 'cyan',
        'SIGNUP': 'green'
      };
      return colors[type] || 'default';
    };

    // Target customers label
    const getTargetCustomersLabel = (target) => {
      const labels = {
        'CERTIFICATE_HOLDERS': '인증서 보유자',
        'ALL': '전체 고객',
        'NON_CERTIFICATE': '비인증서 고객',
        'NEW_CUSTOMERS': '신규 고객'
      };
      return labels[target] || target;
    };

    // Target customers color
    const getTargetCustomersColor = (target) => {
      const colors = {
        'CERTIFICATE_HOLDERS': 'green',
        'ALL': 'blue',
        'NON_CERTIFICATE': 'orange',
        'NEW_CUSTOMERS': 'cyan'
      };
      return colors[target] || 'default';
    };

    // Format event date
    const formatEventDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const getReferralSource = (record) => {
      // referrerId와 referrerId2 값으로 유입 경로 판단
      const hasReferrerId = record.referrerId && record.referrerId !== '';
      const hasReferrerId2 = record.referrerId2 && record.referrerId2 !== '';

      if (!hasReferrerId && !hasReferrerId2) {
        return '인증서 수신자';
      } else if (hasReferrerId && !hasReferrerId2) {
        return '환자의 친구';
      } else if (hasReferrerId && hasReferrerId2) {
        return '친구의 친구';
      }

      return '-';
    };
    
    // 고객분류 라벨 반환
    const getCustomerTypeLabel = (type) => {
      const labels = {
        'THERMAGE_WITH_ZEISS': '써마지+자이스',
        'THERMAGE_ONLY': '써마지만',
        'EXISTING_CERTIFICATE': '기존인증서',
        'NEW_CERTIFICATE': '신규인증서',
        'NON_CERTIFICATE': '인증서미발급',
        'NOT_PARTICIPATED': '미참여'
      };
      return labels[type] || type;
    };

    // 고객분류 색상 반환
    const getCustomerTypeColor = (type) => {
      const colors = {
        'THERMAGE_WITH_ZEISS': 'purple',  // 프리미엄 고객
        'THERMAGE_ONLY': 'orange',        // 써마지만
        'EXISTING_CERTIFICATE': 'blue',    // 기존 고객
        'NEW_CERTIFICATE': 'green',        // 신규 고객
        'NON_CERTIFICATE': 'default',      // 잠재 고객
        'NOT_PARTICIPATED': 'red'          // 비활성
      };
      return colors[type] || 'default';
    };

    // Segment Primary 라벨 반환
    const getSegmentPrimaryLabel = (primary) => {
      const labels = {
        'CERTIFICATE_HOLDER': '인증서 보유 (바이럴)',
        'NON_CERTIFICATE': '인증서 미보유 (잠재고객)',
        'THERMAGE_WITH_ZEISS': '써마지+자이스',
        'THERMAGE_ONLY': '써마지',
        'EXISTING_CERTIFICATE': '기존 고객',
        'NEW_CERTIFICATE': '신규 고객',
        'NOT_PARTICIPATED': '미참여'
      };
      return labels[primary] || primary;
    };

    // Segment Primary 색상 반환
    const getSegmentPrimaryColor = (primary) => {
      const colors = {
        'CERTIFICATE_HOLDER': 'green',
        'NON_CERTIFICATE': 'orange',
        'THERMAGE_WITH_ZEISS': 'purple',
        'THERMAGE_ONLY': 'orange',
        'EXISTING_CERTIFICATE': 'blue',
        'NEW_CERTIFICATE': 'cyan',
        'NOT_PARTICIPATED': 'default'
      };
      return colors[primary] || 'default';
    };

    // Segment Secondary 라벨 반환
    const getSegmentSecondaryLabel = (secondary) => {
      const labels = {
        'EVENT_PARTICIPANT': '이벤트 참여 (응모정보 제출)',
        'NEW_CERTIFICATE': '신규 인증서',
        'EXISTING_CERTIFICATE': '기존 인증서',
        'HIGH_ENGAGEMENT': '높은 참여도',
        'LOW_ENGAGEMENT': '낮은 참여도',
        'SHARED_EVENT': '공유 완료',
        'REFERRER': '추천인',
        'COUPON_WINNER': '쿠폰 당첨'
      };
      return labels[secondary] || secondary;
    };

    // Segment Secondary 색상 반환
    const getSegmentSecondaryColor = (secondary) => {
      const colors = {
        'EVENT_PARTICIPANT': 'blue',
        'NEW_CERTIFICATE': 'green',
        'EXISTING_CERTIFICATE': 'cyan',
        'HIGH_ENGAGEMENT': 'purple',
        'LOW_ENGAGEMENT': 'default',
        'SHARED_EVENT': 'geekblue',
        'REFERRER': 'magenta',
        'COUPON_WINNER': 'gold'
      };
      return colors[secondary] || 'default';
    };

    const getReferralSourceColor = (record) => {
      // referrerId와 referrerId2 값으로 유입 경로의 색상 결정
      const hasReferrerId = record.referrerId && record.referrerId !== '';
      const hasReferrerId2 = record.referrerId2 && record.referrerId2 !== '';

      if (!hasReferrerId && !hasReferrerId2) {
        return 'purple'; // 인증서 수신자 - 보라색
      } else if (hasReferrerId && !hasReferrerId2) {
        return 'blue'; // 환자의 친구 - 파란색
      } else if (hasReferrerId && hasReferrerId2) {
        return 'green'; // 친구의 친구 - 초록색
      }

      return 'default';
    };
    
    // API 호출 메서드들
    const loadEvents = () => {
      eventsLoading.value = true;

      getEventsWithStatus(
        { status: 'all', limit: 100 },
        (response) => {
          if (response.data) {
            // API 응답 구조에 따라 데이터 추출 (items 배열 사용)
            const events = response.data.items || response.data.data || response.data || [];
            // 이벤트 목록을 eventTitle과 eventStatus를 포함하여 저장
            eventList.value = Array.isArray(events) ? events : [];

            // 고정 옵션 추가 - 써마지쿠폰수령
            const thermageOption = {
              _id: 'THERMAGE',
              eventTitle: '써마지쿠폰수령',
              eventStatus: 'ongoing'
            };
            eventList.value.push(thermageOption);

            console.log('이벤트 목록 로드 완료:', eventList.value.length, '개');

            // 이벤트가 선택되지 않은 경우에만 기본 이벤트 자동 선택
            if (eventList.value.length > 0 && !selectedEvent.value) {
              // 1. 쿼리 파라미터에 eventId가 있으면 해당 이벤트 선택
              const queryEventId = route.query.eventId;
              if (queryEventId) {
                const queryEvent = eventList.value.find(e => e._id === queryEventId);
                if (queryEvent) {
                  selectedEvent.value = queryEventId;
                  currentEventData.value = queryEvent;
                  emit('event-change', queryEventId);
                  console.log('쿼리 파라미터 이벤트 선택:', queryEvent.eventTitle);
                  loadParticipants();
                  eventsLoading.value = false;
                  return;
                }
              }

              // 2. 쿼리 파라미터가 없으면 진행중(ongoing) 이벤트 중 가장 최신 것 선택
              const ongoingEvents = eventList.value.filter(e => e.eventStatus === 'ongoing' && e._id !== 'THERMAGE');
              let defaultEvent;
              if (ongoingEvents.length > 0) {
                // createdAt 또는 createAt 기준 내림차순 정렬하여 가장 최신 이벤트 선택
                // (DB 필드명 불일치 대응: createdAt, createAt 둘 다 체크)
                defaultEvent = ongoingEvents.sort((a, b) => {
                  const dateA = new Date(a.createdAt || a.createAt || 0);
                  const dateB = new Date(b.createdAt || b.createAt || 0);
                  return dateB - dateA;
                })[0];
                console.log('가장 최신 진행중 이벤트 선택:', defaultEvent.eventTitle);
              } else {
                // 진행중 이벤트가 없으면 첫 번째 이벤트 선택
                defaultEvent = eventList.value[0];
                console.log('첫 번째 이벤트 자동 선택:', defaultEvent.eventTitle);
              }

              selectedEvent.value = defaultEvent._id;
              currentEventData.value = defaultEvent;
              emit('event-change', defaultEvent._id);
              loadParticipants();
            }
          }
          eventsLoading.value = false;
        },
        (error) => {
          console.error('이벤트 목록 로드 실패:', error);
          message.error('이벤트 목록을 불러오는 중 오류가 발생했습니다.');
          eventsLoading.value = false;
        }
      );
    };
    
    const loadParticipants = () => {
      // 전체 이벤트 검색이 아니고, 이벤트가 선택되지 않은 경우 참여자 데이터를 로드하지 않음
      if (!searchAllEvents.value && (!selectedEvent.value || selectedEvent.value === '')) {
        participantData.value = [];
        pagination.value.total = 0;
        console.log('이벤트가 선택되지 않아 참여자 데이터를 표시하지 않습니다.');
        return Promise.resolve();
      }
      
      const params = {
        page: pagination.value.current,
        limit: pagination.value.pageSize,
        showGlobalLoading: false
      };
      
      // 활동 유형 필터
      if (selectedActivityTypes.value.length > 0) {
        params.activityTypes = selectedActivityTypes.value;
      }
      
      // 검색어 필터 (전화번호일 경우 하이픈 제거)
      if (searchKeyword.value) {
        // 전화번호 형식인 경우 하이픈 제거
        const searchValue = searchKeyword.value.replace(/[^0-9a-zA-Z가-힣]/g, '');
        params.search = /^\d+$/.test(searchValue) ? searchValue : searchKeyword.value;
      }

      // 병원 이름 검색 필터
      if (hospitalSearchKeyword.value) {
        params.hospitalNameFilter = hospitalSearchKeyword.value.trim();
      }

      // 과거 이벤트 참여 이력 필터 추가
      if (selectedEventMetadataFilter.value) {
        params.eventMetadataFilter = selectedEventMetadataFilter.value;
      }

      // 세그먼트 필터 추가
      if (selectedPrimarySegment.value) {
        params.primarySegment = selectedPrimarySegment.value;
      }

      if (selectedSecondarySegment.value && selectedSecondarySegment.value.length > 0) {
        params.secondarySegment = selectedSecondarySegment.value;
      }

      // 쿠폰 필터 추가
      if (selectedCouponInfo.value) {
        params.couponInfoFilter = selectedCouponInfo.value;
      }
      
      if (selectedCouponNumber.value) {
        params.couponNumberFilter = selectedCouponNumber.value;
      }
      
      // 최소 쿠폰 수 필터 추가
      if (minCouponCount.value && minCouponCount.value > 0) {
        params.minCouponCount = minCouponCount.value;
      }
      
      // 리뷰 필터 추가
      if (selectedReviewFilter.value) {
        params.reviewFilter = selectedReviewFilter.value;
      }
      
      // 리뷰 컨텐츠 필터 추가
      if (selectedReviewContentFilter.value) {
        params.reviewContentFilter = selectedReviewContentFilter.value;
      }
      
      // 마케팅 수신 동의 필터 추가
      if (selectedMarketingConsent.value) {
        params.marketingConsentFilter = selectedMarketingConsent.value;
      }

      // 공유 필터 추가
      if (selectedShareFilter.value) {
        params.shareFilter = selectedShareFilter.value;
      }

      // 삭제 여부 필터 추가
      if (selectedDeleteYnFilter.value) {
        params.deleteYnFilter = selectedDeleteYnFilter.value;
      }

      // 공유 횟수 필터 추가
      if (minShareCount.value && minShareCount.value > 0) {
        params.minShareCount = minShareCount.value;
        params.shareCountOperator = shareCountOperator.value;
      }

      // 날짜 필터 추가
      if (selectedDateType.value && selectedDateRange.value && selectedDateRange.value.length === 2) {
        const [startDate, endDate] = selectedDateRange.value;
        const formattedStart = dayjs(startDate).format('YYYY-MM-DD');
        const formattedEnd = dayjs(endDate).format('YYYY-MM-DD');
        
        switch (selectedDateType.value) {
          case 'participatedAt':
            params.participatedAtFrom = formattedStart;
            params.participatedAtTo = formattedEnd;
            break;
          case 'surgeryDate':
            params.surgeryDateFrom = formattedStart;
            params.surgeryDateTo = formattedEnd;
            break;
          case 'couponCreatedAt':
            params.couponCreatedAtFrom = formattedStart;
            params.couponCreatedAtTo = formattedEnd;
            break;
          case 'couponIssuedAt':
            params.couponIssuedAtFrom = formattedStart;
            params.couponIssuedAtTo = formattedEnd;
            break;
          case 'couponExpiryDate':
            params.couponExpiryDateFrom = formattedStart;
            params.couponExpiryDateTo = formattedEnd;
            break;
        }
      }
      
      // 전체 이벤트 검색일 경우 eventId를 null로 설정
      const eventId = searchAllEvents.value ? null : selectedEvent.value;

      return new Promise((resolve, reject) => {
        getEventAttendeesWithActivities(
          eventId,
          params,
          (response) => {
          console.log('참여자 데이터 API 응답:', response.data);
          if (response.data && response.data.data) {
            // API 응답 구조에 따라 데이터 추출 - response.data.data.items
            const responseData = response.data.data;
            const items = responseData.items || [];
            const paginationInfo = responseData.pagination || {};
            const activityTypesObj = responseData.activityTypes || {}; // 활동 유형 객체 추출
            const activityTypesData = activityTypesObj.activityTypes || []; // 실제 배열 추출

            // 예산 통계 저장 (백엔드에서 계산)
            if (responseData.budgetStats) {
              budgetStats.value = responseData.budgetStats;
              console.log('예산 통계 로드 완료:', budgetStats.value);
            } else {
              budgetStats.value = { totalSpentAmount: 0, issuedCouponCount: 0 };
            }
            
            // 활동 유형 데이터 처리
            if (Array.isArray(activityTypesData) && activityTypesData.length > 0) {
              // 카운트 기준 내림차순 정렬 (이미 정렬되어 있을 수 있지만 확실히 하기 위해)
              const sortedData = [...activityTypesData].sort((a, b) => b.count - a.count);
              availableActivityTypes.value = sortedData.map(item => ({
                type: item.type,
                label: `${item.type} (${item.count.toLocaleString()})`,
                count: item.count || 0
              }));
              console.log('활동 유형 데이터 로드 완료:', availableActivityTypes.value.length, '개');
            } else {
              console.log('활동 유형 데이터가 없거나 형식이 맞지 않습니다:', activityTypesObj);
              availableActivityTypes.value = [];
            }
            
            // 각 참여자 데이터를 테이블에 표시할 형식으로 변환
            if (Array.isArray(items)) {
              participantData.value = items.map(item => ({
                ...item,
                key: item._id,
                id: item._id,
                name: item.customerName || '',
                phone: item.customerMobile || '',
                hospital: (() => {
                  const metadatas = item.customerInfo?.companyMetadatas;
                  if (!metadatas || metadatas.length === 0) return '-';
                  // 배열의 마지막 요소가 가장 최근 발행된 병원
                  const latestHospital = metadatas[metadatas.length - 1]?.name || '-';
                  if (metadatas.length === 1) return latestHospital;
                  return `${latestHospital} 외 ${metadatas.length - 1}곳`;
                })(),
                // 병원 목록 원본 데이터 (접기/펼치기용)
                hospitalMetadatas: item.customerInfo?.companyMetadatas || [],
                // 활동 기반 상태 계산
                sharedAt: item.activities?.find(a => a.type === '카카오톡 공유하기')?.timestamp || null,
                shareCount: item.shareCount || 0,  // API에서 직접 제공하는 shareCount 사용
                shareChatList: item.shareChatList || [],  // 공유 채팅 목록
                referredUsers: item.referredUsers || [],  // 추천으로 참여한 사용자 목록
                // 쿠폰 수령 여부: COUPON_ISSUED 상태일 때만 수령으로 판정
                couponReceived: item.couponInfo?.some(c => c.status === 'COUPON_ISSUED') || false,
                couponInfo: item.couponInfo || [],
                couponReceivedAt: item.couponInfo?.find(c => c.status === 'COUPON_ISSUED')?.couponIssuedAt || null,
                loggedIn: false, // API 응답에 로그인 정보가 없으므로 false로 설정
                lastActivity: item.updatedAt || item.createdAt,
                status: determineStatus(item),
                marketingConsent: item.marketingConsent?.isAgreed ? 'agreed' : (item.marketingConsent?.isAgreed === false ? 'revoked' : null) // 마케팅 수신 동의 정보 추가
              }));
              
              // 페이지네이션 정보 업데이트
              pagination.value.total = paginationInfo.total || items.length;
              pagination.value.current = paginationInfo.currentPage || 1;
              
              console.log('참여자 데이터 로드 완료:', participantData.value.length, '명 (전체:', pagination.value.total, '명)');
              resolve();
            } else {
              console.error('items가 배열이 아닙니다:', items);
              participantData.value = [];
              pagination.value.total = 0;
              resolve();
            }
          } else {
            console.error('올바르지 않은 API 응답 구조:', response.data);
            participantData.value = [];
            pagination.value.total = 0;
            resolve();
          }
        },
        (error) => {
          console.error('참여자 데이터 로드 실패:', error);
          message.error('참여자 데이터를 불러오는 중 오류가 발생했습니다.');
          participantData.value = [];
          pagination.value.total = 0;
          reject(error);
        }
      );
      });
    };
    
    // 새로운 필터 메서드들
    const handleEventChange = (value) => {
      selectedEvent.value = value;
      selectedActivityTypes.value = []; // 이벤트 변경 시 활동 유형 초기화
      pagination.value.current = 1; // 페이지를 1로 리셋

      // 현재 이벤트 데이터 업데이트
      const selectedEventObj = eventList.value.find(e => e._id === value);
      if (selectedEventObj) {
        currentEventData.value = selectedEventObj;
        console.log('Current event data updated:', selectedEventObj.eventTitle);
      }

      // URL 쿼리 파라미터 업데이트 (eventId 유지)
      const newQuery = { ...route.query };
      if (value) {
        newQuery.eventId = value;
      } else {
        delete newQuery.eventId;
      }
      router.replace({ query: newQuery });

      emit('event-change', value);
      console.log('Selected event:', value);

      // 참여자 데이터 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleActivityTypeChange = (values) => {
      selectedActivityTypes.value = values;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected activity types:', values);
      
      // 참여자 데이터 다시 로드
      loadParticipants();
    };
    
    
    const handleCouponInfoChange = (value) => {
      selectedCouponInfo.value = value;
      
      // 쿠폰 발급 권한이 "권한 있음"이 아닌 경우 쿠폰 수령 여부 필터 초기화
      if (value !== 'exists') {
        selectedCouponNumber.value = null;
      }
      
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected coupon info filter:', value);
      
      // 참여자 데이터 다시 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleCouponNumberChange = (value) => {
      selectedCouponNumber.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected coupon number filter:', value);
      
      // 참여자 데이터 다시 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleMinCouponCountChange = (value) => {
      minCouponCount.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected min coupon count:', value);
      
      // 참여자 데이터 다시 로드 (활동 유형 포함)
      loadParticipants();
    };
    
    const handleEventMetadataFilterChange = (value) => {
      selectedEventMetadataFilter.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected event metadata filter:', value);

      // 참여자 데이터 다시 로드
      loadParticipants();
    };

    const handlePrimarySegmentChange = (value) => {
      selectedPrimarySegment.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected primary segment:', value);

      // 참여자 데이터 다시 로드
      loadParticipants();
    };

    const handleSecondarySegmentChange = (value) => {
      selectedSecondarySegment.value = value;
      pagination.value.current = 1; // 페이지를 1로 리셋
      console.log('Selected secondary segment:', value);

      // 참여자 데이터 다시 로드
      loadParticipants();
    };

    const handleDateTypeChange = (value) => {
      selectedDateType.value = value;
      // 날짜 유형이 변경되면 날짜 범위는 유지하고 API 호출
      console.log('Selected date type:', value);
      
      if (selectedDateRange.value && selectedDateRange.value.length === 2) {
        pagination.value.current = 1; // 페이지를 1로 리셋
        
        // 참여자 데이터 다시 로드 (활동 유형 포함)
        loadParticipants();
      }
    };
    
    const handleDateRangeChange = (dates) => {
      selectedDateRange.value = dates;
      
      if (dates && dates.length === 2) {
        pagination.value.current = 1; // 페이지를 1로 리셋
        console.log('Selected date range:', dates.map(d => dayjs(d).format('YYYY-MM-DD')));
        
        // 참여자 데이터 다시 로드 (활동 유형 포함)
        loadParticipants();
      } else if (!dates) {
        // 날짜 범위가 clear되면 데이터 다시 로드
        pagination.value.current = 1; // 페이지를 1로 리셋
        loadParticipants();
      }
    };
    
    const handleTableChange = (paginationInfo) => {
      pagination.value = { ...pagination.value, ...paginationInfo };
      loadParticipants();
    };
    
    const resetAllFilters = () => {
      selectedEvent.value = null;
      selectedActivityTypes.value = [];
      searchKeyword.value = '';
      hospitalSearchKeyword.value = '';
      selectedEventMetadataFilter.value = null;
      selectedPrimarySegment.value = null;
      selectedSecondarySegment.value = [];
      selectedCouponInfo.value = null;
      selectedCouponNumber.value = null;
      minCouponCount.value = null;
      selectedReviewFilter.value = null;
      selectedReviewContentFilter.value = null;
      selectedMarketingConsent.value = null;
      selectedShareFilter.value = null;
      minShareCount.value = null;
      shareCountOperator.value = 'gte'; // 기본값으로 리셋
      selectedDeleteYnFilter.value = 'exclude'; // 기본값으로 리셋 (삭제 고객 제외)
      selectedDateType.value = 'participatedAt'; // 기본값으로 리셋
      selectedDateRange.value = null;
      selectedRowKeys.value = [];
      selectedRows.value = [];

      // 데이터 초기화
      availableActivityTypes.value = [];
      participantData.value = [];
      pagination.value.total = 0;

      message.success('모든 필터가 초기화되었습니다.');
    };
    
    // 이벤트 참여 여부를 판단하는 통합 헬퍼 함수
    const hasParticipated = (record) => {
      // _id가 있으면 참여로 간주
      return !!record._id;
    };

    // 쿠폰 툴팁 텍스트 생성 함수
    const getCouponTooltip = (record) => {
      if (!record.couponInfo || record.couponInfo.length === 0) {
        return '쿠폰 발급권한 없음';
      }

      const issuedCount = record.couponInfo.filter(c => c.status === 'COUPON_ISSUED').length;
      const winnerCount = record.couponInfo.filter(c => c.status === 'winner_selected').length;
      const totalCount = record.couponInfo.length;

      if (issuedCount > 0) {
        return `쿠폰 발급완료 (COUPON_ISSUED: ${issuedCount}개)`;
      } else if (winnerCount > 0) {
        return `쿠폰 발급권한 (winner_selected: ${winnerCount}개)`;
      } else {
        return `쿠폰 정보 (${totalCount}개)`;
      }
    };

    // 카카오톡 채팅 타입 이름 변환 함수
    const getChatTypeName = (chatType) => {
      const chatTypeMap = {
        'DirectChat': '친구와 1:1 채팅방',
        'MultiChat': '그룹 채팅방',
        'OpenChat': '오픈 채팅방',
        'MemoChat': '나와의 채팅'
      };
      return chatTypeMap[chatType] || chatType || '채팅방';
    };

    // 참여자 상태를 결정하는 헬퍼 함수
    const determineStatus = (item) => {
      // _id가 없으면 미참여 상태
      if (!hasParticipated(item)) {
        return 'requested';
      }

      // 쿠폰번호가 있으면 실제 쿠폰 수령 상태
      if (item.couponInfo?.some(c => c.couponNumber)) {
        return 'coupon_received';
      }

      // 쿠폰 정보는 있지만 쿠폰번호가 없으면 당첨 대기 상태
      if (item.couponInfo && item.couponInfo.length > 0) {
        return 'coupon_pending';
      }

      // 공유 완료 상태: shareChatList 배열이 있거나, shareCount가 0보다 크거나, sharedEvent가 true인 경우
      const hasShared = (item.shareChatList && item.shareChatList.length > 0) ||
                        (item.shareCount && item.shareCount > 0) ||
                        item.sharedEvent === true ||
                        item.activities?.some(a => a.type === '카카오톡 공유하기');
      if (hasShared) {
        return 'shared';
      }

      // 참여 상태
      return 'participated';
    };
    
    // 날짜 포맷 함수
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return dayjs(dateString).format('YYYY-MM-DD HH:mm');
    };

    // 경과 시간 포맷팅 함수
    const formatElapsedTime = (startTime) => {
      if (!startTime) return '';
      const elapsed = Date.now() - startTime;
      const seconds = Math.floor(elapsed / 1000);
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}분 ${remainingSeconds}초`;
    };
    
    // 당첨알림 발송 핸들러
    const handleSendWinnerNotification = () => {
      // 선택된 참여자 확인
      if (selectedRows.value.length === 0) {
        message.warning('당첨알림을 발송할 참여자를 선택해주세요.');
        return;
      }
      
      // 이벤트 선택 확인
      if (!selectedEvent.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }
      
      // 참여자 ID 추출
      const attendeeIds = selectedRows.value.map(row => row._id || row.id);
      
      // API 호출
      if (apiMethods && apiMethods.sendWinnerNotifications) {
        apiMethods.sendWinnerNotifications(
          selectedEvent.value,
          {
            messageType: 'ALIM_TALK',
            attendeeIds: attendeeIds
          },
          (response) => {
            // API 응답 구조 분석
            const responseData = response.data?.data || response.data;
            const results = responseData.results || [];
            const successCount = responseData.successCount || 0;
            const failedCount = responseData.failedCount || 0;
            
            // 성공/실패 케이스별 메시지 생성
            if (successCount > 0 && failedCount === 0) {
              // 모두 성공
              message.success(`${successCount}명에게 당첨알림이 발송되었습니다.`);
            } else if (successCount === 0 && failedCount > 0) {
              // 모두 실패
              const failReasons = results.map(r => `${r.customerName}: ${r.reason}`).join('\n');
              message.warning(`당첨알림 발송 실패\n${failReasons}`);
            } else if (successCount > 0 && failedCount > 0) {
              // 일부 성공, 일부 실패
              const failedResults = results.filter(r => r.result === 'failed');
              const failReasons = failedResults.map(r => `${r.customerName}: ${r.reason}`).join(', ');
              message.info(`${successCount}명 발송 성공, ${failedCount}명 실패\n실패 사유: ${failReasons}`);
            } else {
              // 처리된 건이 없는 경우
              const summary = responseData.summary;
              message.warning(summary || '처리 가능한 참석자가 없습니다');
            }
            
            // 선택 초기화 및 목록 새로고침
            if (successCount > 0) {
              selectedRowKeys.value = [];
              selectedRows.value = [];
              loadParticipants();
            }
          },
          (error) => {
            message.error('당첨알림 발송 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
          }
        );
      } else {
        message.error('API 초기화 오류가 발생했습니다.');
      }
    };

    // 선택된 행 중 인증서 보유자(CERTIFICATE_HOLDER)가 있는지 확인
    const hasCertificateHolders = computed(() => {
      return selectedRows.value.some(
        row => row.segment?.primary === 'CERTIFICATE_HOLDER'
      );
    });

    // 인증서 알림톡 재발송 핸들러
    const handleResendCertificateNotification = () => {
      console.log('=== 재발송 디버그 ===');
      console.log('selectedRows:', selectedRows.value);
      console.log('첫번째 row 전체:', JSON.stringify(selectedRows.value[0], null, 2));

      // CERTIFICATE_HOLDER 세그먼트의 참여자만 필터링
      const certificateHolders = selectedRows.value.filter(
        row => row.segment?.primary === 'CERTIFICATE_HOLDER'
      );

      console.log('certificateHolders:', certificateHolders.length, '명');
      if (certificateHolders.length > 0) {
        console.log('첫번째 certificateHolder:', JSON.stringify(certificateHolders[0], null, 2));
        console.log('certificates 필드:', certificateHolders[0].certificates);
      }

      if (certificateHolders.length === 0) {
        message.warning('재발송 대상이 없습니다. 인증서 보유자(CERTIFICATE_HOLDER)만 재발송 가능합니다.');
        return;
      }

      // NFT ID 추출 (certificates, certificate, activities에서 추출)
      const nftIds = certificateHolders
        .flatMap(row => {
          // 1. certificates 배열에서 추출 시도
          if (row.certificates?.length > 0) {
            return row.certificates.map(cert => cert.id).filter(id => id);
          }
          // 2. certificate 객체에서 추출 (MQ에서 업데이트된 단일 인증서)
          if (row.certificate?.nftId || row.certificate?.id) {
            return [row.certificate.nftId || row.certificate.id];
          }
          // 3. activities에서 nftNo 추출 (카카오톡 인증서 수신 페이지)
          const certActivity = row.activities?.find(a =>
            a.type === '카카오톡 인증서 수신 페이지' && a.details?.nftNo
          );
          if (certActivity?.details?.nftNo) {
            return certActivity.details.nftNo.split(',').map(id => id.trim());
          }
          return [];
        })
        .filter(id => id); // null/undefined 제거

      console.log('추출된 nftIds:', nftIds);

      if (nftIds.length === 0) {
        message.warning('재발송 대상 인증서가 없습니다.');
        return;
      }

      // 확인 모달
      Modal.confirm({
        title: '인증서 알림톡 재발송',
        content: `선택된 ${certificateHolders.length}명 중 ${nftIds.length}건의 인증서에 알림톡을 재발송합니다.\n\n※ 이미 카카오톡에 저장한 사용자는 자동으로 제외됩니다.`,
        okText: '재발송',
        cancelText: '취소',
        class: 'resend-confirm-modal',
        wrapClassName: 'resend-confirm-modal',
        rootClassName: 'resend-confirm-modal',
        onOk: () => {
          if (apiMethods && apiMethods.resendCertificateNotifications) {
            apiMethods.resendCertificateNotifications(
              nftIds,
              (response) => {
                const data = response.data?.data || response.data;
                const { successCount, failedCount, skippedCount, results } = data;

                // 결과 메시지 생성
                let resultMessage = `재발송 완료\n\n`;
                resultMessage += `✅ 성공: ${successCount}건\n`;
                if (skippedCount > 0) {
                  resultMessage += `⚠️ 건너뜀 (이미 저장): ${skippedCount}건\n`;
                }
                if (failedCount > 0) {
                  resultMessage += `❌ 실패: ${failedCount}건\n`;
                  const failedResults = results.filter(r => r.result === 'failed');
                  if (failedResults.length > 0) {
                    resultMessage += `\n실패 사유:\n`;
                    failedResults.slice(0, 5).forEach(r => {
                      resultMessage += `- ${r.customerName}: ${r.reason}\n`;
                    });
                    if (failedResults.length > 5) {
                      resultMessage += `... 외 ${failedResults.length - 5}건\n`;
                    }
                  }
                }

                if (failedCount > 0 && successCount === 0) {
                  message.warning(resultMessage);
                } else if (failedCount > 0) {
                  message.info(resultMessage);
                } else {
                  message.success(resultMessage);
                }

                // 선택 초기화 및 목록 새로고침
                if (successCount > 0) {
                  selectedRowKeys.value = [];
                  selectedRows.value = [];
                  loadParticipants();
                }
              },
              (error) => {
                message.error('재발송 실패: ' + (error.response?.data?.message || '알 수 없는 오류'));
              }
            );
          } else {
            message.error('API 초기화 오류가 발생했습니다.');
          }
        }
      });
    };

    // 쿠폰 삭제 후 처리
    const handleCouponDeleted = async (coupon) => {
      console.log('쿠폰 삭제됨:', coupon);
      console.log('현재 선택된 이벤트:', selectedEvent.value);
      console.log('참여자 목록 새로고침 시작...');
      
      // 참여자 목록 새로고침
      await loadParticipants();
      
      // 드로워가 열려있는 참여자 데이터 업데이트
      if (selectedParticipant.value) {
        // 새로고침된 데이터에서 해당 참여자 찾기
        const updatedParticipant = participantData.value.find(
          p => (p._id || p.id) === (selectedParticipant.value._id || selectedParticipant.value.id)
        );
        if (updatedParticipant) {
          selectedParticipant.value = { ...updatedParticipant };
          console.log('선택된 참여자 데이터 업데이트됨');
        }
      }
      
      console.log('참여자 목록 새로고침 완료, 총', participantData.value.length, '명');
    };
    
    // 쿠폰 재발송 후 처리
    const handleCouponResent = (coupon) => {
      console.log('쿠폰 재발송됨:', coupon);
      // 참여자 목록 새로고침
      loadParticipants();
    };

    // 드로워 새로고침 처리
    const handleDrawerRefresh = async () => {
      console.log('드로워에서 새로고침 요청');

      // 이벤트 목록 및 참여자 목록 새로고침
      loadEvents();
      await loadParticipants();

      // 드로워가 열려있는 참여자 데이터 업데이트
      if (selectedParticipant.value) {
        // 새로고침된 데이터에서 해당 참여자 찾기
        const updatedParticipant = participantData.value.find(
          p => (p._id || p.id) === (selectedParticipant.value._id || selectedParticipant.value.id)
        );
        if (updatedParticipant) {
          selectedParticipant.value = { ...updatedParticipant };
          console.log('선택된 참여자 데이터 업데이트됨');
        }
      }
    };

    // 컴포넌트 마운트 시 초기 데이터 로드
    onMounted(() => {
      // 이벤트 목록만 먼저 로드
      loadEvents();

      // URL 쿼리 파라미터에서 eventId가 있으면 참여자 데이터도 로드
      if (route.query.eventId) {
        console.log('URL에서 eventId 감지, 참여자 데이터 로드:', route.query.eventId);
        loadParticipants();
      }

      // 반응형: 화면 크기 감지 초기화
      checkScreenSize();
      window.addEventListener('resize', checkScreenSize);
    });

    // 컴포넌트 언마운트 시 정리
    onUnmounted(() => {
      // 폴링 중지
      stopSegmentPolling();
      // 반응형: resize 이벤트 리스너 제거
      window.removeEventListener('resize', checkScreenSize);
    });

    // 이벤트 목록 페이지로 이동
    const goToEventList = () => {
      // router.push 대신 location.href를 사용하여 페이지 새로고침 유도
      window.location.href = '/events/management';
    };

    // 이벤트 수정 페이지로 이동
    const goToEditEvent = () => {
      // 현재 페이지 정보를 포함하여 이벤트 수정 페이지로 이동
      // Vue Router가 query 파라미터를 자동으로 인코딩함
      router.push({
        path: `/events/${selectedEvent.value}/edit`,
        query: {
          from: 'participation',
          returnUrl: router.currentRoute.value.fullPath
        }
      });
    };

    // 동기화 단계 텍스트 변환
    const getCurrentPhaseText = (phase) => {
      const phases = {
        'QUEUED': '대기 중',
        'FETCHING_NFTS': 'NFT 조회 중',
        'PROCESSING': '처리 중'
      };
      return phases[phase] || phase || '준비 중';
    };

    // 세그먼트 동기화 처리
    const handleSegmentSync = async () => {
      if (!selectedEvent.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      // 대량 처리 경고
      const totalCount = filteredData.value.length;
      if (totalCount > 1000) {
        const confirmResult = await new Promise((resolve) => {
          Modal.confirm({
            title: '대량 동기화 안내',
            content: `${totalCount.toLocaleString()}명의 참여자 세그먼트를 동기화합니다. 5-10분 소요될 수 있습니다. 계속하시겠습니까?`,
            okText: '동기화 시작',
            cancelText: '취소',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          });
        });
        if (!confirmResult) return;
      }

      // 동기화 시작 API 호출
      segmentSyncStatus.value = 'processing';
      segmentSyncProgress.value = {
        remainingCount: 0,
        syncedCount: 0,
        totalCount: 0,
        percentage: 0
      };

      instance.proxy.$apiInstance.post(`/events-v2/segments/sync/event/${selectedEvent.value}`, {
        confirmed: true
      }, {
        showGlobalLoading: false  // 전역 로딩 비활성화
      }).then((response) => {
        const data = response.data?.data || response.data;
        message.info('세그먼트 동기화가 시작되었습니다.');

        // 폴링 시작
        startSegmentPolling();
      }).catch((error) => {
        console.error('세그먼트 동기화 시작 실패:', error);
        segmentSyncStatus.value = 'failed';
        message.error('세그먼트 동기화 시작에 실패했습니다: ' + (error.response?.data?.message || '알 수 없는 오류'));
      });
    };

    // 세그먼트 폴링 시작
    const startSegmentPolling = () => {
      // 기존 폴링 정리
      if (segmentPollingInterval.value) {
        clearInterval(segmentPollingInterval.value);
      }

      // 폴링 카운트 및 시작 시간 초기화
      segmentPollingCount.value = 0;
      segmentPollingStartTime.value = Date.now();
      segmentElapsedTime.value = formatElapsedTime(segmentPollingStartTime.value);

      // 5초 간격으로 상태 조회
      segmentPollingInterval.value = setInterval(() => {
        segmentPollingCount.value++;
        // 경과 시간 업데이트
        segmentElapsedTime.value = formatElapsedTime(segmentPollingStartTime.value);

        // 최대 시도 횟수 또는 최대 시간 체크
        const elapsedTime = Date.now() - segmentPollingStartTime.value;
        if (segmentPollingCount.value >= MAX_POLLING_COUNT || elapsedTime >= MAX_POLLING_TIME) {
          stopSegmentPolling();
          segmentSyncStatus.value = 'failed';
          message.error('세그먼트 동기화 시간 초과 (10분 경과)');

          // 3초 후 초기화
          setTimeout(() => {
            segmentSyncStatus.value = 'idle';
            segmentSyncProgress.value = {
              remainingCount: 0,
              syncedCount: 0,
              totalCount: 0,
              percentage: 0
            };
          }, 3000);
          return;
        }

        // 상태 조회 API 호출
        instance.proxy.$apiInstance.get(`/events-v2/segments/sync/event/${selectedEvent.value}/status`, {
          showGlobalLoading: false  // 전역 로딩 비활성화
        })
          .then((response) => {
            const data = response.data?.data || response.data;

            // 진행 상황 업데이트
            const syncedCount = data.syncedCount || 0;
            const totalCount = data.totalCount || 0;
            const remainingCount = data.remainingCount || 0;

            // 진행률 계산: syncedCount / totalCount
            const calculatedPercentage = totalCount > 0 ? Math.round((syncedCount / totalCount) * 100) : 0;

            segmentSyncProgress.value = {
              remainingCount: remainingCount,
              syncedCount: syncedCount,
              totalCount: totalCount,
              percentage: data.progress?.percentage || calculatedPercentage
            };

            // 완료 체크
            if (!data.hasPendingJob && data.needsSync === false) {
              stopSegmentPolling();
              segmentSyncStatus.value = 'completed';
              message.success(`세그먼트 동기화 완료: ${data.syncedCount}명 처리됨`);

              // 테이블 새로고침
              loadParticipants();

              // 3초 후 상태 초기화
              setTimeout(() => {
                segmentSyncStatus.value = 'idle';
                segmentSyncProgress.value = {
                  remainingCount: 0,
                  syncedCount: 0,
                  totalCount: 0,
                  percentage: 0
                };
              }, 3000);
            }
          })
          .catch((error) => {
            // 네트워크 에러는 무시하고 계속 폴링
            console.error('세그먼트 상태 조회 실패:', error);
          });
      }, 5000); // 5초 간격
    };

    // 세그먼트 폴링 중지
    const stopSegmentPolling = () => {
      if (segmentPollingInterval.value) {
        clearInterval(segmentPollingInterval.value);
        segmentPollingInterval.value = null;
      }
      // 폴링 관련 상태 초기화
      segmentPollingCount.value = 0;
      segmentPollingStartTime.value = null;
      segmentElapsedTime.value = '';
    };

    // ========================================
    // 부하테스트 기능
    // ========================================
    const loadTestModalVisible = ref(false);
    const loadTestRunning = ref(false);
    const loadTestConfig = ref({
      requestsPerSecond: 5,
      totalRequests: 20,
      phoneMode: 'random', // 'random': 랜덤 번호 (부하 테스트), 'fixed': 고정 번호 (Rate Limit 테스트)
      fixedPhone: '01012345678' // 고정 모드 시 사용할 번호
    });
    const loadTestResults = ref({
      success: 0,
      failed: 0,
      rateLimited: 0, // 429 에러
      errors: [],
      rawResponses: [] // 모든 응답 raw 데이터
    });
    const showRawResponses = ref(false); // raw 응답 표시 토글
    // 예상 결과 계산 (시뮬레이션)
    const loadTestExpected = computed(() => {
      const { totalRequests, phoneMode } = loadTestConfig.value;
      const rateLimitPerMinute = 3; // Rate Limit: 분당 3회/전화번호

      if (phoneMode === 'random') {
        // 랜덤 번호: 각각 다른 번호이므로 모두 성공 예상
        return {
          success: totalRequests,
          rateLimited: 0,
          description: '각 요청마다 다른 전화번호를 사용하므로 Rate Limit이 적용되지 않습니다.'
        };
      } else {
        // 고정 번호: 분당 3회 제한 적용
        const expectedSuccess = Math.min(totalRequests, rateLimitPerMinute);
        const expectedRateLimited = Math.max(0, totalRequests - rateLimitPerMinute);
        return {
          success: expectedSuccess,
          rateLimited: expectedRateLimited,
          description: `동일 전화번호로 분당 ${rateLimitPerMinute}회까지만 허용됩니다.`
        };
      }
    });

    // 부하테스트 모달 열기
    const showLoadTestModal = () => {
      if (!selectedEvent.value) {
        message.warning('먼저 이벤트를 선택해주세요.');
        return;
      }
      // 결과 초기화
      loadTestResults.value = {
        success: 0,
        failed: 0,
        rateLimited: 0,
        errors: [],
        rawResponses: []
      };
      showRawResponses.value = false;
      loadTestModalVisible.value = true;
    };

    // 부하테스트 실행
    const runLoadTest = async () => {
      if (!selectedEvent.value) {
        message.error('이벤트가 선택되지 않았습니다.');
        return;
      }

      loadTestRunning.value = true;
      loadTestResults.value = {
        success: 0,
        failed: 0,
        rateLimited: 0,
        errors: [],
        rawResponses: []
      };

      const apiUrl = process.env.VUE_APP_SERVERURL || 'https://intest-inapi.certi.life';
      const eventId = selectedEvent.value;
      const { requestsPerSecond, totalRequests, phoneMode, fixedPhone } = loadTestConfig.value;

      // 초당 요청 간격 계산 (ms)
      const intervalMs = Math.floor(1000 / requestsPerSecond);
      let completedRequests = 0;

      const sendRequest = async (index) => {
        // phoneMode에 따라 전화번호 결정
        const testPhone = phoneMode === 'fixed'
          ? fixedPhone
          : `010${String(Math.floor(Math.random() * 100000000)).padStart(8, '0')}`;
        const testData = {
          eventId: eventId,
          customerName: `테스트${index}`,
          customerMobile: testPhone,
          marketingConsent: false
        };

        try {
          const response = await fetch(`${apiUrl}/events-v2/attendees/upsert`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(testData)
          });

          const responseData = await response.json().catch(() => ({}));

          // raw 응답 저장
          loadTestResults.value.rawResponses.push({
            index,
            status: response.status,
            phone: testPhone,
            request: testData,
            response: responseData
          });

          if (response.status === 429) {
            loadTestResults.value.rateLimited++;
          } else if (response.ok) {
            loadTestResults.value.success++;
          } else {
            loadTestResults.value.failed++;
            loadTestResults.value.errors.push({
              index,
              status: response.status,
              message: responseData.message || 'Unknown error'
            });
          }
        } catch (error) {
          loadTestResults.value.failed++;
          loadTestResults.value.errors.push({
            index,
            status: 0,
            message: error.message || 'Network error'
          });
          loadTestResults.value.rawResponses.push({
            index,
            status: 0,
            phone: testPhone,
            request: testData,
            response: { error: error.message }
          });
        }

        completedRequests++;
      };

      // 요청 스케줄링
      for (let i = 0; i < totalRequests; i++) {
        setTimeout(() => {
          sendRequest(i + 1);
        }, i * intervalMs);
      }

      // 모든 요청 완료 대기
      const totalTimeMs = totalRequests * intervalMs + 3000; // 여유 시간 3초
      setTimeout(() => {
        loadTestRunning.value = false;
        message.info(
          `부하테스트 완료: 성공 ${loadTestResults.value.success}, ` +
          `429 ${loadTestResults.value.rateLimited}, ` +
          `실패 ${loadTestResults.value.failed}`
        );
      }, totalTimeMs);
    };

    // ========================================
    // 중복응모 테스트 (원자성 검증)
    // ========================================
    const duplicateTestModalVisible = ref(false);
    const duplicateTestRunning = ref(false);
    const duplicateTestTarget = ref(null); // 테스트 대상 참여자
    const duplicateTestConfig = ref({
      concurrentRequests: 10 // 동시 요청 수
    });
    const duplicateTestResults = ref({
      total: 0,
      success: 0,
      alreadyRegistered: 0, // 이미 등록됨 응답
      rateLimited: 0,
      failed: 0,
      couponCount: null, // 발급된 쿠폰 수 (API 조회 결과)
      errors: []
    });

    // 중복응모 테스트 모달 열기
    const showDuplicateTestModal = (record) => {
      if (!record.customerMobile) {
        message.warning('전화번호가 없는 참여자입니다.');
        return;
      }
      duplicateTestTarget.value = record;
      // 결과 초기화
      duplicateTestResults.value = {
        total: 0,
        success: 0,
        alreadyRegistered: 0,
        rateLimited: 0,
        failed: 0,
        couponCount: null,
        errors: []
      };
      duplicateTestModalVisible.value = true;
    };

    // 중복응모 테스트 실행
    const runDuplicateTest = async () => {
      if (!duplicateTestTarget.value || !selectedEvent.value) {
        message.error('테스트 대상 또는 이벤트가 선택되지 않았습니다.');
        return;
      }

      duplicateTestRunning.value = true;
      duplicateTestResults.value = {
        total: duplicateTestConfig.value.concurrentRequests,
        success: 0,
        alreadyRegistered: 0,
        rateLimited: 0,
        failed: 0,
        couponCount: null,
        errors: []
      };

      const apiUrl = process.env.VUE_APP_SERVERURL || 'https://intest-inapi.certi.life';
      const eventId = selectedEvent.value;
      const { concurrentRequests } = duplicateTestConfig.value;
      const targetPhone = duplicateTestTarget.value.customerMobile;
      const targetName = duplicateTestTarget.value.customerName || '테스트';

      // 동시에 모든 요청 발송
      const requests = [];
      for (let i = 0; i < concurrentRequests; i++) {
        const testData = {
          eventId: eventId,
          customerName: targetName,
          customerMobile: targetPhone,
          marketingConsent: false
        };

        requests.push(
          fetch(`${apiUrl}/events-v2/attendees/upsert`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(testData)
          })
            .then(async (response) => {
              if (response.status === 429) {
                duplicateTestResults.value.rateLimited++;
                return { status: 429 };
              } else if (response.ok) {
                const data = await response.json().catch(() => ({}));
                // 응답에서 쿠폰 발급 여부 확인
                if (data.isNewRegistration === false || data.message?.includes('이미')) {
                  duplicateTestResults.value.alreadyRegistered++;
                } else {
                  duplicateTestResults.value.success++;
                }
                return { status: 200, data };
              } else {
                duplicateTestResults.value.failed++;
                return { status: response.status };
              }
            })
            .catch((error) => {
              duplicateTestResults.value.failed++;
              duplicateTestResults.value.errors.push(error.message);
              return { status: 0, error: error.message };
            })
        );
      }

      // 모든 요청 완료 대기
      await Promise.all(requests);

      // 결과 메시지
      const results = duplicateTestResults.value;
      const isAtomicityValid = results.success <= 1; // 성공(신규등록)이 1개 이하면 원자성 정상

      duplicateTestRunning.value = false;

      if (isAtomicityValid) {
        message.success(
          `원자성 검증 성공! ${concurrentRequests}회 동시 요청 → ` +
          `신규등록 ${results.success}회, 이미등록 ${results.alreadyRegistered}회`
        );
      } else {
        message.error(
          `원자성 검증 실패! ${concurrentRequests}회 동시 요청 → ` +
          `신규등록 ${results.success}회 (1회 초과)`
        );
      }
    };

    return {
      // 반응형 상태
      isMobile,
      isTablet,
      isFilterCollapsed,
      isEventInfoCollapsed,
      isPerformanceAnalysisCollapsed,
      searchKeyword,
      hospitalSearchKeyword,
      selectedRowKeys,
      selectedRows,
      selectedRowKeysSet,
      selectedEvent,
      selectedActivityTypes,
      searchAllEvents,
      selectedCouponInfo,
      selectedCouponNumber,
      minCouponCount,
      selectedEventMetadataFilter,
      selectedPrimarySegment,
      selectedSecondarySegment,
      selectedReviewFilter,
      selectedReviewContentFilter,
      selectedMarketingConsent,
      selectedDateType,
      selectedDateRange,
      eventList,
      availableActivityTypes,
      groupedActivityTypes,
      groupTotalCounts,
      currentEventData,
      eventsLoading,
      activeFilterCount,
      activeFilterTags,
      rewardThresholdTags,
      collapsedGroups,
      toggleGroup,
      columns,
      stats,
      spentBudget,
      isSpentBudgetExpanded,
      isTotalBudgetExpanded,
      filteredData,
      pagination,
      detailDrawerVisible,
      selectedParticipant,
      messageDrawerVisible,
      messageRecipients,
      deleteDrawerVisible,
      showResourceDrawer,
      thermageMigrationDrawerVisible,
      organizerCertMigrationDrawerVisible,
      apiInstance,
      handleFilterChange,
      formatPhoneNumberInput,
      handleSearch,
      handleHospitalSearch,
      handleHospitalSearchChange,
      handleSearchAllEventsChange,
      handleRefresh,
      onSelectChange,
      handleSelectAll,
      handleCardSelect,
      handleMobilePageChange,
      handleSendMessage,
      handleSendWinnerNotification,
      handleResendCertificateNotification,
      hasCertificateHolders,
      handleDeleteAttendees,
      handleDeleteSuccess,
      handleThermageMigrationComplete,
      sendIndividualMessage,
      viewDetail,
      resetParticipationInfo,
      handleSingleEventClick,
      copyEventId,
      copyCustomerId,
      copyEventAttendeesId,
      copyDeviceId,
      copyReferrerId,
      copyReferrerId2,
      copyPatientLink,
      copyFriendLink,
      copyReviewLink,
      handleCouponDeleted,
      handleCouponResent,
      handleDrawerRefresh,
      hasParticipated,
      getCouponTooltip,
      getChatTypeName,
      toggleEventExpand,
      getDisplayedEvents,
      expandedEvents,
      toggleHospitalExpand,
      getDisplayedHospitals,
      expandedHospitals,
      getStatusColor,
      getStatusText,
      getEventStatusText,
      getEventStatusColor,
      getScenarioTypeLabel,
      getScenarioTypeColor,
      getTargetCustomersLabel,
      getTargetCustomersColor,
      formatEventDate,
      getConditionTypeLabel,
      getConditionTypeColor,
      getTargetAudienceLabel,
      getTargetAudienceColor,
      getCustomerTypeLabel,
      getCustomerTypeColor,
      getSegmentPrimaryLabel,
      getSegmentPrimaryColor,
      getSegmentSecondaryLabel,
      getSegmentSecondaryColor,
      getReferralSource,
      getReferralSourceColor,
      handleEventChange,
      handleActivityTypeChange,
      handleCouponInfoChange,
      handleCouponNumberChange,
      handleMinCouponCountChange,
      handleEventMetadataFilterChange,
      handlePrimarySegmentChange,
      handleSecondarySegmentChange,
      handleDateTypeChange,
      handleDateRangeChange,
      handleReviewFilterChange,
      handleReviewContentFilterChange,
      handleMarketingConsentChange,
      handleShareFilterChange,
      handleDeleteYnFilterChange,
      handleMinShareCountChange,
      handleShareCountOperatorChange,
      selectedShareFilter,
      selectedDeleteYnFilter,
      minShareCount,
      shareCountOperator,
      handleTableChange,
      resetAllFilters,
      formatDate,
      handleMessageSent,
      goToEventList,
      goToEditEvent,
      getCurrentPhaseText,
      handleSegmentSync,
      segmentSyncStatus,
      segmentSyncProgress,
      segmentSyncButtonText,
      segmentElapsedTime,
      // 부하테스트
      loadTestModalVisible,
      loadTestRunning,
      loadTestConfig,
      loadTestResults,
      loadTestExpected,
      showRawResponses,
      showLoadTestModal,
      runLoadTest,
      // 중복응모 테스트 (원자성 검증)
      duplicateTestModalVisible,
      duplicateTestRunning,
      duplicateTestTarget,
      duplicateTestConfig,
      duplicateTestResults,
      showDuplicateTestModal,
      runDuplicateTest
    };
  }
};
</script>

<style lang="scss" scoped>
// 변수 정의
$primary-color: #1890ff;
$success-color: #52c41a;
$warning-color: #faad14;
$error-color: #f5222d;
$gray-light: #fafafa;
$gray-base: #999;
$gray-dark: #595959;
$text-secondary: #666;
$background-hover: #f5f5f5;

// 믹스인 정의
@mixin full-width {
  width: 100%;
}

@mixin text-align-right {
  text-align: right;
}

@mixin flex-between {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

// 메인 컨테이너
.participant-monitoring {
  padding: 20px;

  // 필터 섹션
  .filter-section {
    background: $gray-light;
    padding: 20px;
    border-radius: 8px;
    transition: all 0.3s ease;

    // 필터 헤더
    .filter-header {
      padding-bottom: 0;

      .filter-title {
        font-size: 16px;
        font-weight: 600;
        color: $gray-dark;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #d4b106;
        }
      }

      .collapse-button {
        color: $text-secondary;

        &:hover {
          color: #d4b106;
        }
      }

      .filter-tags-summary {
        margin-top: 12px;
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        align-items: center;

        .ant-tag {
          margin: 0;
          font-size: 12px;
        }

        // 이벤트 태그 + ID 복사 버튼 컨테이너
        .event-tag-with-id {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: #f0f5ff;
          border-radius: 4px;
          padding: 2px 4px 2px 0;

          .event-tag-label {
            white-space: nowrap;
            max-width: none;
            overflow: visible;
          }

          .copy-id-button {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            padding: 0 6px;
            height: 22px;
            font-size: 11px;
            color: #1890ff;
            border-radius: 3px;
            background: #e6f4ff;

            &:hover {
              background: #bae0ff;
              color: #0958d9;
            }

            i {
              font-size: 10px;
            }
          }
        }
      }
    }

    // 필터 콘텐츠
    .filter-content {
      padding-top: 16px;
      border-top: 1px solid #e8e8e8;
      margin-top: 16px;
    }

    .filter-row {
      margin-bottom: 0;

      &.mt-3 {
        margin-top: 16px;
      }

      // 우측 정렬 컬럼
      .col-right-align {
        @include text-align-right;
      }

      // 필터 액션 버튼 그룹
      .filter-actions {
        margin-top: 28px; // 라벨 높이 보정
      }
    }

    .filter-label {
      display: block;
      font-size: 12px;
      color: $gray-dark;
      margin-bottom: 6px;
      font-weight: 500;
    }

    // 모든 select, input 요소
    .filter-input {
      @include full-width;
    }

    // 이벤트 ID 표시
    .event-id-text {
      margin-left: 8px;

      .copyable-text {
        color: $text-secondary;
        font-size: 12px;
      }
    }

    // 필터 개수 배지
    .filter-count-badge {
      .ant-badge-count {
        background-color: $success-color;
      }
    }
  }

  // 이벤트 기본정보 섹션
  .event-info-section {
    background: $gray-light;
    padding: 20px;
    border-radius: 8px;
    transition: all 0.3s ease;

    // 이벤트 기본정보 헤더
    .event-info-header {
      padding-bottom: 0;

      .event-info-title {
        font-size: 16px;
        font-weight: 600;
        color: $gray-dark;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #1890ff;
        }
      }

      .collapse-button {
        color: $text-secondary;

        &:hover {
          color: #1890ff;
        }
      }

      // 헤더 좌측 영역 (타이틀 + 보상조건 태그)
      .event-info-header-left {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
      }

      // 접힌 상태에서 보상조건 태그 요약
      .reward-tags-summary {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 6px;
        padding: 4px 10px;
        background: #f5f5f5;
        border-radius: 4px;
        border-left: 3px solid #faad14;

        .reward-tags-label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 4px;

          i {
            color: #faad14;
          }
        }

        .ant-tag {
          margin: 0;
          cursor: pointer;
          transition: all 0.2s;

          &:hover {
            transform: scale(1.05);
          }
        }

        .reward-inactive-tag {
          opacity: 0.6;
          text-decoration: line-through;
        }
      }
    }

    // 이벤트 기본정보 콘텐츠
    .event-info-content {
      padding-top: 16px;
      border-top: 1px solid #e8e8e8;
      margin-top: 16px;
    }

    // 기본 정보 그리드
    .event-basic-info {
      background: #fff;
      padding: 16px;
      border-radius: 6px;
      border: 1px solid #e8e8e8;

      .info-item {
        display: flex;
        align-items: flex-start;
        padding: 8px 0;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
          border-bottom: none;
        }

        .info-label {
          font-size: 13px;
          color: $gray-base;
          min-width: 100px;
          flex-shrink: 0;
        }

        .info-value {
          font-size: 13px;
          color: $gray-dark;
          flex: 1;
          word-break: break-word;

          &.event-title-value {
            font-weight: 600;
            font-size: 14px;
          }
        }
      }
    }

    // 보상조건 서브섹션
    .reward-subsection {
      margin-top: 20px;

      .subsection-title {
        font-size: 14px;
        font-weight: 600;
        color: $gray-dark;
        margin-bottom: 12px;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #d4b106;
        }
      }
    }

    // 보상조건 카드
    .reward-card {
      height: 100%;
      border: 1px solid #e8e8e8;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      &.reward-inactive {
        opacity: 0.6;
        background-color: #f5f5f5;
      }

      .reward-card-title {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .reward-card-body {
        .reward-name {
          font-size: 14px;
          font-weight: 600;
          color: #262626;
          margin-bottom: 4px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .reward-description {
          font-size: 12px;
          color: $text-secondary;
          line-height: 1.4;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 34px;
        }

        .reward-details {
          .reward-detail-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 6px;

            &:last-child {
              margin-bottom: 0;
            }

            .detail-label {
              font-size: 12px;
              color: $gray-base;
            }

            .detail-value {
              font-size: 12px;
              color: $gray-dark;

              .issued-count {
                font-weight: 600;
                color: $primary-color;
              }

              .max-count {
                color: $gray-base;
              }
            }
          }
        }
      }
    }
  }

  // 성과분석 섹션
  .performance-analysis-section {
    background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
    padding: 20px;
    border-radius: 8px;
    transition: all 0.3s ease;
    border: 1px solid #d6e4ff;

    // 성과분석 헤더
    .performance-analysis-header {
      padding-bottom: 0;

      .performance-analysis-title {
        font-size: 16px;
        font-weight: 600;
        color: $gray-dark;
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          color: #1890ff;
        }
      }

      .collapse-button {
        color: $text-secondary;

        &:hover {
          color: #1890ff;
        }
      }

      .performance-analysis-header-left {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
      }

      .performance-summary-tags {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
      }
    }

    // 성과분석 콘텐츠
    .performance-analysis-content {
      margin-top: 20px;
      padding-top: 20px;
      border-top: 1px solid #d6e4ff;
    }

    // 성과 통계 카드
    .performance-stat-card {
      display: flex;
      align-items: center;
      gap: 16px;
      background: #fff;
      padding: 16px 20px;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
      }

      .stat-icon {
        width: 48px;
        height: 48px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;

        &.budget-icon {
          background: linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%);
          color: #1890ff;
        }

        &.spent-icon {
          background: linear-gradient(135deg, #fff7e6 0%, #ffe7ba 100%);
          color: #fa8c16;
        }

        &.remaining-icon {
          background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
          color: #52c41a;
        }
      }

      .stat-info {
        display: flex;
        flex-direction: column;
        gap: 4px;

        .stat-label {
          font-size: 13px;
          color: $gray-base;
        }

        .stat-value {
          font-size: 20px;
          font-weight: 700;
          color: $gray-dark;
        }

        .stat-header {
          display: flex;
          align-items: center;
          gap: 8px;
        }
      }

      // 확장 가능한 카드 스타일
      &.expandable-card {
        cursor: pointer;
        align-items: flex-start;

        &.expanded {
          background: linear-gradient(135deg, #fffbe6 0%, #fff 100%);
        }

        .stat-info {
          flex: 1;
        }

        .spent-detail {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed #e8e8e8;
          width: 100%;

          .detail-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 6px 0;

            &:not(:last-child) {
              border-bottom: 1px dotted #f0f0f0;
            }

            .detail-label {
              font-size: 12px;
              color: #666;
              display: flex;
              align-items: center;
              gap: 6px;

              i {
                font-size: 11px;
                color: #999;
              }
            }

            .detail-value {
              font-size: 13px;
              font-weight: 600;
              color: #333;
            }
          }
        }
      }
    }

    // 예산 소진율 프로그레스 바
    .budget-progress-section {
      background: #fff;
      padding: 16px 20px;
      border-radius: 8px;

      .progress-label {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        font-size: 14px;
        color: $gray-dark;

        .progress-percentage {
          font-weight: 600;
          color: #1890ff;
        }
      }
    }
  }

  // 테이블 섹션
  .table-section {
    .table-header {
      padding: 0 8px;

      .col-right-align {
        @include text-align-right;
      }
    }

    // 과거 이벤트 참여이력 셀
    .event-metadata-cell {
      font-size: 12px;
      line-height: 1.4;

      .event-metadata-header {
        @include flex-between;
        margin-bottom: 4px;

        .event-count {
          font-weight: 600;
          color: $primary-color;
        }

        .expand-button {
          padding: 0;
          height: auto;
          font-size: 11px;

          .chevron-icon {
            margin-left: 4px;
          }
        }
      }

      .event-item {
        margin-bottom: 6px;
        padding: 4px;
        background: $background-hover;
        border-radius: 4px;
        cursor: pointer;
        transition: background 0.3s;

        &:hover {
          background: darken($background-hover, 5%);
        }

        .event-title {
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .event-details {
          display: flex;
          gap: 8px;
          margin-top: 2px;
          flex-wrap: wrap;

          .event-date {
            color: $text-secondary;
          }

          .status-check {
            color: $success-color;
          }

          .status-x {
            color: $gray-base;
          }

          .coupon-received {
            color: $success-color;
          }

          .coupon-pending {
            color: $warning-color;
          }

          .no-coupon {
            color: $gray-base;
          }
        }
      }

      .no-metadata {
        color: $gray-base;
      }
    }

    // 병원 셀
    .hospital-cell {
      font-size: 12px;
      line-height: 1.4;

      .hospital-header {
        @include flex-between;
        margin-bottom: 4px;

        .hospital-count {
          font-weight: 600;
          color: $primary-color;
        }

        .expand-button {
          padding: 0;
          height: auto;
          font-size: 11px;
        }
      }

      .hospital-item {
        margin-bottom: 4px;
        padding: 4px 6px;
        background: $background-hover;
        border-radius: 4px;

        .hospital-name {
          font-weight: 500;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .hospital-date {
          font-size: 11px;
          color: $text-secondary;
          margin-top: 2px;
        }
      }

      .hospital-more {
        font-size: 11px;
        color: $text-secondary;
        text-align: center;
        padding: 2px 0;
      }
    }

    // 인증서 셀
    .certificates-cell {
      display: flex;
      justify-content: center;
      align-items: center;

      .certificate-icon {
        font-size: 18px;
        color: $success-color;
      }

      .no-certificate {
        color: #ccc;
      }
    }
  }

  // 진행 상태 인디케이터
  .progress-indicators {
    display: flex;
    align-items: center;
    gap: 4px;

    .indicator {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #f0f0f0;
      color: $gray-base;
      font-size: 12px;
      transition: all 0.3s;
      position: relative;

      &.active {
        background: #d4b106;
        color: white;
      }

      &.coupon-pending {
        background: #1890ff;
        color: white;
      }

      // 공유 횟수 표시
      .share-count {
        position: absolute;
        top: -6px;
        right: -6px;
        background: $error-color;
        color: white;
        border-radius: 10px;
        padding: 2px 6px;
        font-size: 10px;
        font-weight: 600;
        min-width: 16px;
        text-align: center;
        line-height: 1;
      }
    }

    .indicator-line {
      width: 20px;
      height: 2px;
      background: #f0f0f0;
      transition: all 0.3s;

      &.active {
        background: #d4b106;
      }

      &.coupon-pending {
        background: #1890ff;
      }
    }
  }

  // 노란색 버튼 스타일 (프로젝트 공통)
  .yellow-button {
    background-color: #FFD814;
    border-color: #FFD814;

    &:hover {
      background-color: #E5C112;
      border-color: #E5C112;
    }
  }

  // 동기화 진행 배너
  .sync-progress-banner {
    margin-bottom: 20px;

    .sync-banner-content {
      @include flex-between;
      font-size: 16px;
      font-weight: 600;

      .sync-title {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .sync-stats {
        color: $gray-dark;

        .sync-time {
          margin-left: 8px;
          color: #8c8c8c;
          font-size: 14px;
          font-weight: normal;
        }
      }
    }

    .sync-details {
      padding-top: 8px;

      .ant-progress {
        margin-bottom: 12px;
      }

      .ant-col {
        font-size: 14px;
        color: $gray-dark;
      }
    }
  }

  // 액티비티 체크박스 그룹
  .activity-checkbox-group {
    background: $background-hover;
    padding: 20px;
    border-radius: 6px;

    // AARRR 그룹들을 2열로 배치
    .aarrr-groups-container {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;
      width: 100%;
    }

    .activity-group {
      margin-bottom: 0;
      padding: 16px;
      background: #ffffff;
      border: 1px solid #e8e8e8;
      border-radius: 8px;

      .group-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 2px solid #f0f0f0;
        user-select: none;

        &:hover {
          background-color: #fafafa;
        }

        .collapse-icon {
          font-size: 12px;
          color: #8c8c8c;
          transition: transform 0.3s ease;
        }

        i {
          font-size: 16px;
          color: $primary-color;
        }

        .group-title {
          font-weight: 600;
          font-size: 14px;
          color: #262626;
        }
      }

      .group-content {
        padding-left: 16px;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }

      .activity-item {
        display: flex;
        align-items: flex-start;
        padding: 4px 0;
        min-width: 0; // 텍스트 오버플로우 방지

        :deep(.ant-checkbox-wrapper) {
          width: 100%;

          .ant-checkbox {
            flex-shrink: 0;
          }
        }
      }
    }

    .activity-label {
      font-size: 14px;
      display: inline-block;
      line-height: 1.5;
      word-break: break-word;
    }

    // 체크박스 스타일
    :deep(.ant-checkbox-wrapper) {
      display: flex;
      align-items: center;
      padding: 4px 0;
      width: 100%;

      &:hover {
        color: #d4b106;
      }
    }

    :deep(.ant-checkbox-checked .ant-checkbox-inner) {
      background-color: #d4b106;
      border-color: #d4b106;
    }
  }

  // Help 아이콘 스타일
  .help-icon {
    color: #8c8c8c;
    margin-left: 4px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
      color: #d4b106;
    }
  }

  // =========================================
  // 반응형 스타일
  // =========================================
  $breakpoint-mobile: 767px;
  $breakpoint-tablet: 1199px;

  @mixin mobile {
    @media screen and (max-width: $breakpoint-mobile) {
      @content;
    }
  }

  @mixin tablet {
    @media screen and (min-width: 768px) and (max-width: $breakpoint-tablet) {
      @content;
    }
  }

  // 반응형 버튼 텍스트 숨김
  .btn-text-hide-mobile {
    @include mobile {
      display: none;
    }
  }

  .btn-text-show-mobile {
    display: none;

    @include mobile {
      display: inline;
    }
  }

  // 필터 액션 영역
  .filter-actions-col {
    text-align: right;

    @include mobile {
      text-align: left;
      margin-top: 8px;
    }
  }

  .filter-actions-space {
    margin-top: 28px;

    @include mobile {
      margin-top: 0;
    }
  }

  // 테이블 헤더 반응형
  .table-header-right {
    text-align: right;

    @include mobile {
      text-align: left;
      margin-top: 8px;
    }
  }

  .table-action-space {
    @include mobile {
      width: 100%;
    }
  }

  // 글로벌 액션 영역 스타일
  .global-action-space {
    justify-content: flex-end;

    @include mobile {
      width: 100%;
      justify-content: flex-start;
    }
  }

  // 액션 구분선 스타일
  .action-divider {
    height: 20px;
    margin: 0 4px;
    border-color: #d9d9d9;

    @include mobile {
      display: none;
    }
  }

  // 더보기 드롭다운 메뉴 스타일
  .more-actions-menu {
    min-width: 180px;

    .ant-dropdown-menu-item-group-title {
      font-size: 12px;
      color: #8c8c8c;
      font-weight: 600;
      padding: 8px 12px 4px;
    }

    .ant-dropdown-menu-item {
      padding: 8px 12px;

      i {
        margin-right: 8px;
        width: 14px;
        text-align: center;
        color: #595959;
      }

      &:hover i {
        color: #d4b106;
      }
    }

    // 개발자 도구 아이템 스타일
    .dev-tool-item {
      color: #8c8c8c;

      i {
        color: #faad14;
      }

      &:hover {
        color: #595959;
        background-color: #fff7e6;
      }
    }
  }

  // 이벤트 ID 표시 반응형
  .event-id-display {
    margin-left: 8px;

    @include mobile {
      display: block;
      margin-left: 0;
      margin-top: 4px;
    }
  }

  // =========================================
  // 모바일 카드 리스트 스타일
  // =========================================
  .mobile-card-list {
    padding: 0;

    .mobile-list-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px 0;
      border-bottom: 1px solid #e8e8e8;
      margin-bottom: 12px;

      .total-count {
        font-size: 14px;
        color: #666;
      }
    }

    .mobile-empty {
      padding: 40px 0;
    }

    .mobile-cards-container {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .participant-card {
      background: #fff;
      border: 1px solid #e8e8e8;
      border-radius: 8px;
      overflow: hidden;
      transition: all 0.3s ease;

      &.selected {
        border-color: #d4b106;
        box-shadow: 0 0 0 2px rgba(212, 177, 6, 0.2);
      }

      .card-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px;
        background: #fafafa;
        border-bottom: 1px solid #f0f0f0;

        .card-index {
          font-size: 12px;
          color: #999;
          min-width: 24px;
        }

        .card-segment {
          margin-left: auto;
        }
      }

      .card-body {
        padding: 12px;
        cursor: pointer;

        &:active {
          background: #fafafa;
        }

        .card-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 6px 0;
          border-bottom: 1px solid #f5f5f5;

          &:last-child {
            border-bottom: none;
          }

          .card-label {
            font-size: 12px;
            color: #999;
            flex-shrink: 0;
            width: 60px;
          }

          .card-value {
            font-size: 14px;
            color: #333;
            text-align: right;
            flex: 1;
          }
        }

        .card-progress {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          justify-content: flex-end;

          .mini-indicator {
            font-size: 11px;
            padding: 2px 6px;
            border-radius: 4px;
            background: #f0f0f0;
            color: #999;

            &.active {
              background: #d4b106;
              color: #fff;
            }
          }
        }
      }

      .card-footer {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding: 10px 12px;
        background: #fafafa;
        border-top: 1px solid #f0f0f0;
      }
    }

    .mobile-pagination {
      display: flex;
      justify-content: center;
      padding: 16px 0;
      margin-top: 12px;
    }
  }

  // 반응형 패딩 조정
  @include mobile {
    padding: 12px;
  }

  @include tablet {
    padding: 16px;
  }
} // .participant-monitoring 닫기

// 부하테스트 모달 스타일
.load-test-modal {
  padding: 8px 24px 24px 24px;

  .form-hint {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
  }

  .test-calculation-summary {
    background: linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%);
    border: 1px solid #91d5ff;
    border-radius: 8px;
    padding: 16px;
    margin: 16px 0;

    .calculation-header {
      font-size: 13px;
      font-weight: 600;
      color: #1890ff;
      margin-bottom: 12px;
    }

    .calculation-detail {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      flex-wrap: wrap;
      margin-bottom: 12px;

      .calc-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        background: white;
        padding: 8px 16px;
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        .calc-label {
          font-size: 11px;
          color: #999;
        }

        .calc-value {
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }

        &.calc-result {
          background: #1890ff;

          .calc-label {
            color: rgba(255, 255, 255, 0.8);
          }

          .calc-value {
            color: white;
          }
        }
      }

      .calc-operator {
        font-size: 18px;
        font-weight: 600;
        color: #999;
      }
    }

    .calculation-description {
      text-align: center;
      font-size: 13px;
      color: #666;
      line-height: 1.5;
    }

    // 예상 결과 시뮬레이션
    .expected-results {
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px dashed #91d5ff;

      .expected-header {
        font-size: 13px;
        font-weight: 600;
        color: #722ed1;
        margin-bottom: 10px;

        i {
          margin-right: 6px;
        }
      }

      .expected-detail {
        display: flex;
        gap: 16px;
        justify-content: center;
        margin-bottom: 10px;

        .expected-item {
          font-size: 14px;
          font-weight: 500;
          padding: 6px 12px;
          border-radius: 4px;

          i {
            margin-right: 4px;
          }

          &.success {
            background: #f6ffed;
            color: #3f8600;
          }

          &.rate-limited {
            background: #fff1f0;
            color: #cf1322;
          }
        }
      }

      .expected-description {
        text-align: center;
        font-size: 12px;
        color: #999;
      }
    }
  }

  .radio-hint {
    font-size: 11px;
    color: #999;
    margin-left: 4px;
  }

  .load-test-results {
    background: #fafafa;
    padding: 16px;
    border-radius: 8px;

    // 비교 테이블
    .comparison-table {
      margin-bottom: 16px;

      .comparison-row {
        display: flex;
        border-bottom: 1px solid #e8e8e8;

        &.header {
          background: #fafafa;
          font-weight: 600;
          font-size: 12px;
          color: #666;
        }

        .comparison-cell {
          flex: 1;
          padding: 10px 8px;
          text-align: center;

          &.label {
            text-align: left;
            font-weight: 500;

            i {
              margin-right: 6px;
            }
          }

          &.expected {
            color: #722ed1;
            font-weight: 500;
          }

          &.actual {
            color: #1890ff;
            font-weight: 600;
          }

          &.match {
            font-size: 16px;
          }
        }
      }
    }

    // 종합 판정
    .test-verdict {
      margin-top: 12px;
    }

    // Raw 응답 섹션
    .raw-responses-section {
      margin-top: 16px;
      border-top: 1px solid #e8e8e8;
      padding-top: 8px;

      .raw-responses-list {
        margin-top: 12px;
        max-height: 300px;
        overflow-y: auto;
        border: 1px solid #e8e8e8;
        border-radius: 4px;

        .raw-response-item {
          padding: 8px 12px;
          border-bottom: 1px solid #f0f0f0;
          font-size: 12px;

          &:last-child {
            border-bottom: none;
          }

          &.success {
            background: #f6ffed;
          }

          &.rate-limited {
            background: #fff7e6;
          }

          &.failed {
            background: #fff1f0;
          }

          .response-header {
            display: flex;
            gap: 12px;
            align-items: center;
            margin-bottom: 6px;

            .response-index {
              font-weight: 600;
              color: #666;
            }

            .response-status {
              padding: 2px 8px;
              border-radius: 3px;
              font-weight: 600;
              font-size: 11px;

              &.status-200, &.status-201 {
                background: #52c41a;
                color: white;
              }

              &.status-429 {
                background: #faad14;
                color: white;
              }

              &.status-400, &.status-404, &.status-500 {
                background: #ff4d4f;
                color: white;
              }
            }

            .response-phone {
              color: #999;
              font-family: monospace;
            }
          }

          .response-body {
            margin: 0;
            padding: 8px;
            background: rgba(0, 0, 0, 0.02);
            border-radius: 4px;
            font-size: 11px;
            line-height: 1.4;
            overflow-x: auto;
            white-space: pre-wrap;
            word-break: break-all;
          }
        }
      }
    }
  }
}

// 중복응모 테스트 모달 스타일
.duplicate-test-modal {
  padding: 8px 24px 24px 24px;

  .form-hint {
    font-size: 12px;
    color: #999;
    margin-top: 4px;
  }

  .target-info {
    background: #f6f8fa;
    padding: 12px;
    border-radius: 8px;
  }

  .duplicate-test-results {
    background: #fafafa;
    padding: 16px;
    border-radius: 8px;
  }
}
</style>

<!-- 이벤트 Select 드롭다운 스타일 (portal로 렌더링되므로 unscoped) -->
<style lang="scss">
.event-select-dropdown {
  .ant-select-item-option-content {
    white-space: normal;
    word-break: break-word;
    line-height: 1.4;
    padding: 4px 0;
  }
}

// 재발송 확인 모달 스타일
.resend-confirm-modal {
  .ant-modal-content {
    padding: 20px !important;
  }

  .ant-modal-body {
    padding: 20px !important;
  }

  .ant-modal-confirm-body-wrapper {
    padding: 0 !important;
  }

  .ant-modal-confirm-btns {
    display: flex !important;
    flex-direction: row !important;
    justify-content: flex-start !important;
    margin-top: 16px !important;

    .ant-btn {
      margin-right: 8px !important;
      margin-left: 0 !important;
    }

    .ant-btn-primary {
      background-color: #ffd700 !important;
      border-color: #ffd700 !important;
      color: #000 !important;

      &:hover {
        background-color: #ffcc00 !important;
        border-color: #ffcc00 !important;
      }
    }
  }
}
</style>